import React, { useState, useEffect, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import {
  ShoppingCart, Phone, Mail, MapPin, Check, Plus, Minus, Trash2,
  ChevronRight, Award, Shield, Activity, Users, Building2, Menu, ArrowRight,
  Heart, Brain, User, MessageCircle, CheckCircle, Sparkles, Clock,
  Footprints, ArrowUpDown, Target, X, HelpCircle, ChevronLeft, Star, Upload, Image as ImageIcon,
  Send, Bot, Loader2, FileText, Newspaper, ExternalLink, Stethoscope
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// SEO Components
import { SEO, FAQSchema, ProductSchema, BreadcrumbSchema } from "@/components/SEO";

// Refactored components
import { CartProvider, useCart } from "@/context/CartContext";
import { Navigation, Footer, ChatWidget } from "@/components";
import { API, iconMap, LEGAL_INFO } from "@/lib/constants";

// ============ REVEAL ON SCROLL HOOK ============
const useRevealOnScroll = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    document.querySelectorAll('.reveal-on-scroll, .stagger-children, .animate-card-reveal, .step-card, .why-trust-card').forEach(el => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);
};

// ============ CONSULTATION FORM ============
const ConsultationForm = ({ isB2B = false, compact = false }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', task: '', contact_method: 'phone', is_b2b: isB2B, company_name: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/consultations`, formData);
      toast.success('Заявка отправлена. Мы свяжемся с вами в ближайшее время.');
      setFormData({ name: '', phone: '', task: '', contact_method: 'phone', is_b2b: isB2B, company_name: '' });
    } catch { toast.error('Ошибка при отправке'); }
    finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} className={`space-y-5 ${compact ? '' : 'bg-white rounded-3xl p-8 shadow-lg shadow-black/[0.03] border border-[#f1f5f9]'}`} data-testid="consultation-form">
      {!compact && <h3 className="text-2xl font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Manrope' }}>Получить консультацию</h3>}
      {isB2B && (
        <div>
          <label className="block text-sm font-medium text-[#374151] mb-1.5">Название организации</label>
          <input value={formData.company_name} onChange={(e) => setFormData(p => ({ ...p, company_name: e.target.value }))} placeholder="ООО «Клиника»" className="input-premium" data-testid="consultation-company" />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1.5">Имя *</label>
        <input required value={formData.name} onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))} placeholder="Ваше имя" className="input-premium" data-testid="consultation-name" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1.5">Телефон *</label>
        <input required type="tel" value={formData.phone} onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))} placeholder="+7 (___) ___-__-__" className="input-premium" data-testid="consultation-phone" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1.5">Ваша задача или вопрос</label>
        <Textarea value={formData.task} onChange={(e) => setFormData(p => ({ ...p, task: e.target.value }))} placeholder="Опишите, с чем нужна помощь" rows={3} className="rounded-xl border-[#e2e8f0]" data-testid="consultation-task" />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#374151] mb-1.5">Удобный способ связи</label>
        <div className="flex gap-3">
          {['phone', 'whatsapp', 'telegram'].map(m => (
            <button key={m} type="button" onClick={() => setFormData(p => ({ ...p, contact_method: m }))}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${formData.contact_method === m ? 'bg-[#0f172a] text-white' : 'bg-[#f8fafc] text-[#475569] hover:bg-[#f1f5f9]'}`}>
              {m === 'phone' ? 'Звонок' : m === 'whatsapp' ? 'WhatsApp' : 'Telegram'}
            </button>
          ))}
        </div>
      </div>
      <button type="submit" className="w-full btn-navy h-14" disabled={loading} data-testid="submit-consultation-btn">{loading ? 'Отправка...' : 'Отправить заявку'}</button>
      <p className="text-xs text-[#94a3b8] text-center">Нажимая кнопку, вы соглашаетесь с политикой конфиденциальности</p>
    </form>
  );
};

// ============ QUIZ MODAL ============
const QuizModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [products, setProducts] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', contact_method: 'phone' });
  const [submitted, setSubmitted] = useState(false);

  const questions = [
    {
      id: 'task',
      title: 'Какая у вас основная задача?',
      subtitle: 'Выберите наиболее подходящий вариант',
      options: [
        { value: 'spine', label: 'Поддержка позвоночника и осанки', icon: Activity, tags: ['spine-support'] },
        { value: 'recovery', label: 'Восстановление после травмы или операции', icon: Heart, tags: ['movement-recovery', 'sports-rehab'] },
        { value: 'muscle', label: 'Работа с мышцами, снятие напряжения', icon: Target, tags: ['muscle-tissue'] },
        { value: 'prevention', label: 'Профилактика при сидячей работе', icon: Clock, tags: ['spine-support', 'orthopedic-support'] },
        { value: 'sport', label: 'Спорт: восстановление после нагрузок', icon: Activity, tags: ['sports-rehab', 'muscle-tissue'] },
        { value: 'neuro', label: 'Нейроподдержка, сон, концентрация', icon: Brain, tags: ['neuro-support'] }
      ]
    },
    {
      id: 'location',
      title: 'Где планируете использовать?',
      subtitle: 'Это поможет подобрать подходящий формат',
      options: [
        { value: 'home', label: 'Дома', icon: User, modifier: 'home' },
        { value: 'clinic', label: 'В клинике или кабинете', icon: Building2, modifier: 'clinical' },
        { value: 'sport', label: 'В спортзале или фитнес-центре', icon: Activity, modifier: 'sport' }
      ]
    },
    {
      id: 'severity',
      title: 'Насколько выражена проблема?',
      subtitle: 'Это влияет на интенсивность решения',
      options: [
        { value: 'prevention', label: 'Профилактика — проблемы пока нет', icon: Shield, priority: 'low' },
        { value: 'moderate', label: 'Умеренный дискомфорт', icon: Target, priority: 'medium' },
        { value: 'serious', label: 'Серьёзная проблема, требует внимания', icon: Heart, priority: 'high' }
      ]
    },
    {
      id: 'age',
      title: 'Ваша возрастная группа?',
      subtitle: 'Некоторые решения лучше подходят для разных возрастов',
      options: [
        { value: 'young', label: 'До 35 лет', icon: User, ageGroup: 'young' },
        { value: 'middle', label: '35–55 лет', icon: User, ageGroup: 'middle' },
        { value: 'senior', label: '55+ лет', icon: User, ageGroup: 'senior' }
      ]
    }
  ];

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${API}/products`);
        setProducts(res.data);
      } catch (e) { console.error(e); }
    };
    if (isOpen) fetchProducts();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setStep(0);
      setAnswers({});
      setShowResults(false);
      setSubmitted(false);
      setFormData({ name: '', phone: '', contact_method: 'phone' });
    }
  }, [isOpen]);

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({ ...prev, [questionId]: option }));
    
    if (step < questions.length - 1) {
      setTimeout(() => setStep(step + 1), 300);
    } else {
      calculateRecommendations({ ...answers, [questionId]: option });
    }
  };

  const calculateRecommendations = (finalAnswers) => {
    let scored = products.map(product => {
      let score = 0;
      
      // Task matching - primary filter
      const taskAnswer = finalAnswers.task;
      if (taskAnswer?.tags?.some(tag => product.category_id === tag)) {
        score += 50; // High score for matching task
      }
      
      // Flagship products get bonus
      if (product.is_flagship) score += 15;
      
      // Age group adjustments
      const ageAnswer = finalAnswers.age;
      if (ageAnswer?.ageGroup === 'senior') {
        // Prefer gentler solutions for seniors
        if (product.id === 'kompressionniy-matras' || product.id === 'ortopedicheskie-stelki') score += 10;
      }
      if (ageAnswer?.ageGroup === 'young') {
        // Young people might prefer more active solutions
        if (product.id === 'antigravitacionnye-botinki' || product.id === 'massazhnye-roliki') score += 10;
      }
      
      // Severity adjustments
      const severityAnswer = finalAnswers.severity;
      if (severityAnswer?.priority === 'high' && product.is_flagship) score += 10;
      if (severityAnswer?.priority === 'low' && !product.is_flagship) score += 5;
      
      return { ...product, score };
    });
    
    // Sort by score and filter to show only relevant (score > 0)
    scored.sort((a, b) => b.score - a.score);
    const relevant = scored.filter(p => p.score > 0);
    
    // Show all relevant products, or top 4 if many match
    setRecommendations(relevant.length > 0 ? relevant : scored.slice(0, 4));
    setShowResults(true);
  };

  const handleSubmitConsultation = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Build task description from quiz answers
    const taskDescription = `
Результаты опросника:
- Задача: ${answers.task?.label || 'не указано'}
- Использование: ${answers.location?.label || 'не указано'}
- Выраженность: ${answers.severity?.label || 'не указано'}
- Возраст: ${answers.age?.label || 'не указано'}
- Рекомендованные товары: ${recommendations.map(p => p.name).join(', ')}
    `.trim();
    
    try {
      await axios.post(`${API}/consultations`, {
        name: formData.name,
        phone: formData.phone,
        task: taskDescription,
        contact_method: formData.contact_method,
        is_b2b: false
      });
      setSubmitted(true);
      toast.success('Заявка отправлена! Мы свяжемся с вами.');
    } catch { toast.error('Ошибка при отправке'); }
    finally { setLoading(false); }
  };

  const progress = ((step + 1) / questions.length) * 100;
  const currentQuestion = questions[step];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto p-0">
        {!showResults ? (
          <>
            {/* Progress Bar */}
            <div className="px-6 pt-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-[#64748b]">Шаг {step + 1} из {questions.length}</span>
              </div>
              <div className="h-1.5 bg-[#e2e8f0] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#14b8a6] rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Question */}
            <div className="p-6">
              <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>
                {currentQuestion.title}
              </h2>
              <p className="text-[#64748b] mb-6">{currentQuestion.subtitle}</p>

              {/* Options */}
              <div className="space-y-3">
                {currentQuestion.options.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => handleAnswer(currentQuestion.id, option)}
                    className={`w-full flex items-center gap-4 p-4 rounded-xl border-2 text-left transition-all hover:border-[#14b8a6] hover:bg-[#f8fafc] ${
                      answers[currentQuestion.id]?.value === option.value 
                        ? 'border-[#14b8a6] bg-[#f0fdfa]' 
                        : 'border-[#e2e8f0]'
                    }`}
                    data-testid={`quiz-option-${option.value}`}
                  >
                    {option.icon && (
                      <div className="w-10 h-10 bg-[#f8fafc] rounded-xl flex items-center justify-center flex-shrink-0">
                        <option.icon className="w-5 h-5 text-[#0f172a]" />
                      </div>
                    )}
                    <span className="font-medium text-[#0f172a]">{option.label}</span>
                    {answers[currentQuestion.id]?.value === option.value && (
                      <CheckCircle className="w-5 h-5 text-[#14b8a6] ml-auto" />
                    )}
                  </button>
                ))}
              </div>

              {/* Back Button */}
              {step > 0 && (
                <button 
                  onClick={() => setStep(step - 1)}
                  className="mt-6 flex items-center gap-2 text-[#64748b] hover:text-[#0f172a] transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" /> Назад
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Results */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-[#14b8a6]/10 rounded-xl flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-[#14b8a6]" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>
                    Ваши рекомендации
                  </h2>
                  <p className="text-sm text-[#64748b]">На основе ваших ответов</p>
                </div>
              </div>

              {/* Recommended Products */}
              <div className="space-y-3 mb-8">
                {recommendations.map((product, idx) => (
                  <div 
                    key={product.id}
                    className="flex items-center gap-4 p-4 bg-[#f8fafc] rounded-xl"
                    data-testid={`quiz-recommendation-${idx}`}
                  >
                    <div className="relative">
                      {idx === 0 && (
                        <div className="absolute -top-1 -left-1 w-5 h-5 bg-[#14b8a6] rounded-full flex items-center justify-center text-white text-xs font-bold">
                          1
                        </div>
                      )}
                      <img 
                        src={product.image_url} 
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg"
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-[#0f172a] truncate">{product.name}</h3>
                      <p className="text-sm text-[#64748b] truncate">{product.tagline}</p>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-[#0f172a]">{product.price.toLocaleString()} ₽</div>
                      <Link 
                        to={`/product/${product.slug}`}
                        onClick={onClose}
                        className="text-sm text-[#14b8a6] hover:underline"
                      >
                        Подробнее
                      </Link>
                    </div>
                  </div>
                ))}
              </div>

              {/* Consultation Form */}
              {!submitted ? (
                <div className="bg-[#0f172a] rounded-2xl p-6">
                  <h3 className="text-lg font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>
                    Получить консультацию эксперта
                  </h3>
                  <p className="text-[#94a3b8] text-sm mb-4">
                    Оставьте контакт — поможем выбрать оптимальное решение
                  </p>
                  <form onSubmit={handleSubmitConsultation} className="space-y-3">
                    <input 
                      required
                      value={formData.name}
                      onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                      placeholder="Ваше имя"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-[#14b8a6]"
                      data-testid="quiz-form-name"
                    />
                    <input 
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                      placeholder="+7 (___) ___-__-__"
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder:text-white/50 focus:outline-none focus:border-[#14b8a6]"
                      data-testid="quiz-form-phone"
                    />
                    <div className="flex gap-2">
                      {['phone', 'whatsapp', 'telegram'].map(m => (
                        <button 
                          key={m}
                          type="button"
                          onClick={() => setFormData(p => ({ ...p, contact_method: m }))}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.contact_method === m 
                              ? 'bg-[#14b8a6] text-white' 
                              : 'bg-white/10 text-white/70 hover:bg-white/20'
                          }`}
                        >
                          {m === 'phone' ? 'Звонок' : m === 'whatsapp' ? 'WhatsApp' : 'Telegram'}
                        </button>
                      ))}
                    </div>
                    <button 
                      type="submit" 
                      className="w-full btn-teal h-12"
                      disabled={loading}
                      data-testid="quiz-submit-btn"
                    >
                      {loading ? 'Отправка...' : 'Получить консультацию'}
                    </button>
                  </form>
                </div>
              ) : (
                <div className="bg-[#f0fdfa] rounded-2xl p-6 text-center">
                  <div className="w-16 h-16 bg-[#14b8a6] rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>
                    Заявка отправлена!
                  </h3>
                  <p className="text-[#64748b] mb-4">
                    Наш эксперт свяжется с вами в ближайшее время
                  </p>
                  <button onClick={onClose} className="btn-navy">
                    Закрыть
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ REVIEW FORM ============
const ReviewForm = ({ isOpen, onClose }) => {
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [name, setName] = useState('');
  const [text, setText] = useState('');
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const [photoUrl, setPhotoUrl] = useState(null);

  const handleFile = useCallback(async (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Файл слишком большой. Максимум 5MB');
        return;
      }
      setPhoto(file);
      const reader = new FileReader();
      reader.onload = (e) => setPhotoPreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Upload to server
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData);
        setPhotoUrl(res.data.url);
      } catch (error) {
        console.error('Upload error:', error);
      }
    } else {
      toast.error('Пожалуйста, загрузите изображение');
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFile(file);
  }, [handleFile]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    handleFile(file);
  };

  const removePhoto = () => {
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoUrl(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !text.trim()) {
      toast.error('Заполните имя и текст отзыва');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/reviews`, {
        author_name: name,
        rating,
        text,
        photo_url: photoUrl
      });
      setSubmitted(true);
      toast.success('Спасибо за отзыв!');
    } catch (error) {
      toast.error('Ошибка при отправке отзыва');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setRating(5);
    setName('');
    setText('');
    setPhoto(null);
    setPhotoPreview(null);
    setPhotoUrl(null);
    setSubmitted(false);
  };

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        {!submitted ? (
          <>
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>
                Оставить отзыв
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-5 mt-4">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-2">Ваша оценка</label>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setRating(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 transition-transform hover:scale-110"
                    >
                      <Star 
                        className={`w-8 h-8 ${
                          star <= (hoverRating || rating) 
                            ? 'fill-[#f59e0b] text-[#f59e0b]' 
                            : 'text-[#e2e8f0]'
                        } transition-colors`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Ваше имя *</label>
                <input 
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Как вас зовут"
                  className="input-premium"
                  required
                />
              </div>

              {/* Text */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Ваш отзыв *</label>
                <Textarea 
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder="Расскажите о вашем опыте использования оборудования"
                  rows={4}
                  className="rounded-xl border-[#e2e8f0]"
                  required
                />
              </div>

              {/* Photo Upload */}
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Ваше фото (необязательно)</label>
                
                {!photoPreview ? (
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    className={`
                      relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer
                      transition-all duration-200
                      ${isDragging 
                        ? 'border-[#14b8a6] bg-[#f0fdfa]' 
                        : 'border-[#e2e8f0] hover:border-[#14b8a6]/50 hover:bg-[#f8fafc]'
                      }
                    `}
                  >
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileInput}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    />
                    <div className="flex flex-col items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-colors ${
                        isDragging ? 'bg-[#14b8a6]/20' : 'bg-[#f1f5f9]'
                      }`}>
                        {isDragging ? (
                          <Upload className="w-6 h-6 text-[#14b8a6]" />
                        ) : (
                          <ImageIcon className="w-6 h-6 text-[#64748b]" />
                        )}
                      </div>
                      <div>
                        <p className="text-[#0f172a] font-medium">
                          {isDragging ? 'Отпустите для загрузки' : 'Перетащите фото сюда'}
                        </p>
                        <p className="text-sm text-[#64748b] mt-1">или нажмите для выбора</p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="relative">
                    <img 
                      src={photoPreview} 
                      alt="Preview" 
                      className="w-full h-48 object-cover rounded-xl"
                    />
                    <button
                      type="button"
                      onClick={removePhoto}
                      className="absolute top-2 right-2 p-2 bg-white/90 rounded-lg shadow-md hover:bg-white transition-colors"
                    >
                      <X className="w-4 h-4 text-[#64748b]" />
                    </button>
                  </div>
                )}
              </div>

              <button 
                type="submit" 
                className="w-full btn-teal h-14" 
                disabled={loading}
              >
                {loading ? 'Отправка...' : 'Отправить отзыв'}
              </button>
              
              <p className="text-xs text-[#94a3b8] text-center">
                Отзыв будет опубликован после модерации
              </p>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-[#14b8a6]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-[#14b8a6]" />
            </div>
            <h3 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>
              Спасибо за отзыв!
            </h3>
            <p className="text-[#64748b] mb-6">
              Ваш отзыв отправлен на модерацию и скоро появится на сайте
            </p>
            <button onClick={onClose} className="btn-navy">
              Закрыть
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ============ REVIEW CARD WITH EXPAND ============
const ReviewCard = ({ review }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    if (textRef.current) {
      setIsOverflowing(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, [review.text]);

  return (
    <div className={`bg-[#f8fafc] rounded-2xl p-6 border transition-all duration-300 ${review.is_featured ? 'border-[#14b8a6] ring-1 ring-[#14b8a6]/20' : 'border-[#e2e8f0]/60'} ${isExpanded ? 'md:col-span-2 lg:col-span-1' : ''}`}>
      <div className="flex gap-1 mb-4">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star 
            key={star}
            className={`w-4 h-4 ${star <= review.rating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-[#e2e8f0]'}`}
          />
        ))}
      </div>
      <div className="relative">
        <p 
          ref={textRef}
          className={`text-[#475569] mb-4 leading-relaxed transition-all duration-300 ${isExpanded ? '' : 'line-clamp-4'}`}
        >
          "{review.text}"
        </p>
        {(isOverflowing || isExpanded) && (
          <button 
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-[#14b8a6] text-sm font-medium hover:text-[#0d9488] transition-colors"
          >
            {isExpanded ? 'Свернуть' : 'Читать полностью'}
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 mt-4">
        {review.photo_url ? (
          <img src={review.photo_url} alt={review.author_name} className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 bg-gradient-to-br from-[#14b8a6] to-[#0d9488] rounded-full flex items-center justify-center text-white font-semibold">
            {review.author_name?.charAt(0) || 'К'}
          </div>
        )}
        <span className="font-medium text-[#0f172a]">{review.author_name}</span>
      </div>
    </div>
  );
};

// ============ HOME PAGE ============
const HomePage = () => {
  const [categories, setCategories] = useState([]);
  const [flagshipProducts, setFlagshipProducts] = useState([]);
  const [approvedReviews, setApprovedReviews] = useState([]);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showPaymentSuccess, setShowPaymentSuccess] = useState(false);
  const [paymentOrderId, setPaymentOrderId] = useState('');
  const [deliveryType, setDeliveryType] = useState('delivery');
  const navigate = useNavigate();
  const location = useLocation();

  // Enable reveal on scroll animations
  useRevealOnScroll();

  // Check for payment success redirect
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const paymentStatus = params.get('payment');
    const orderId = params.get('order');
    const type = params.get('type');
    
    if (paymentStatus === 'success' && orderId) {
      setPaymentOrderId(orderId);
      setDeliveryType(type || 'delivery');
      setShowPaymentSuccess(true);
      // Clean URL
      window.history.replaceState({}, '', '/');
    } else if (paymentStatus === 'failed') {
      toast.error('Оплата не прошла. Попробуйте ещё раз или свяжитесь с нами.');
      window.history.replaceState({}, '', '/');
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, prodRes, reviewsRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/products?flagship=true`),
          axios.get(`${API}/reviews?approved_only=true`)
        ]);
        setCategories(catRes.data);
        setFlagshipProducts(prodRes.data);
        setApprovedReviews(reviewsRes.data);
      } catch (error) { console.error('Error:', error); }
    };
    fetchData();
  }, []);

  const expertFeatures = [
    { 
      title: "Врач-реабилитолог в команде", 
      desc: "Каждое решение подбирается с учётом рекомендаций специалиста с 20-летним опытом в области восстановительной медицины",
      icon: Award
    },
    { 
      title: "Связь с клиникой", 
      desc: "Возможность очной консультации и тестирования оборудования перед покупкой",
      icon: Building2
    },
    { 
      title: "Обучение применению", 
      desc: "Не просто продажа — показываем технику, объясняем противопоказания, сопровождаем на всех этапах",
      icon: Target
    }
  ];

  const audiences = [
    { icon: Activity, title: "Восстановление и реабилитация", desc: "После травм, операций, интенсивных нагрузок" },
    { icon: User, title: "Поддержка осанки и позвоночника", desc: "Боли в спине, шее, нарушения осанки" },
    { icon: Clock, title: "Сидячий образ жизни", desc: "Профилактика для офисных работников и водителей" },
    { icon: Target, title: "Спорт и фитнес", desc: "Восстановление, подготовка, профилактика травм" },
    { icon: Heart, title: "Функциональное здоровье", desc: "Поддержка движения и качества жизни" },
    { icon: Building2, title: "Клиники и специалисты", desc: "Профессиональное оснащение кабинетов" }
  ];

  const solutions = [
    { 
      title: "Для спины и разгрузки", 
      desc: "Профилакторы, системы декомпрессии, поддержка позвоночника", 
      img: "/images/solution-spine.png", 
      link: "/catalog?category=spine-support" 
    },
    { 
      title: "Для мышц и мягких тканей", 
      desc: "Массажные инструменты, ролики, вакуумные банки", 
      img: "/images/solution-muscle.png", 
      link: "/catalog?category=muscle-tissue" 
    },
    { 
      title: "Для опоры и повседневной поддержки", 
      desc: "Ортопедические стельки, поддержка стопы и суставов", 
      img: "/images/solution-support.png", 
      link: "/catalog?category=orthopedic-support" 
    },
    { 
      title: "Для сна, внимания и нейроподдержки", 
      desc: "Нейротренажёры, устройства для сна и концентрации", 
      img: "/images/solution-neuro.png", 
      link: "/catalog?category=neuro-support" 
    }
  ];

  const whyTrust = [
    { 
      num: "01",
      title: "Экспертная платформа, а не маркетплейс", 
      desc: "Мы не продаём всё подряд. В каталоге только оборудование, которое прошло проверку практикой и рекомендуется нашим специалистом."
    },
    { 
      num: "02",
      title: "Подбор под вашу задачу", 
      desc: "Прежде чем предложить решение, мы уточняем цель, особенности и ограничения. Если что-то не подходит — честно скажем."
    },
    { 
      num: "03",
      title: "Обучение и сопровождение", 
      desc: "Для большинства решений важна правильная техника. Показываем, как применять, и отвечаем на вопросы после покупки."
    },
    { 
      num: "04",
      title: "Широкий спектр задач", 
      desc: "Восстановление, поддержка движения, осанка, мышцы, нейроподдержка — комплексный подход к функциональному здоровью."
    }
  ];

  const steps = [
    { num: "1", title: "Заявка", desc: "Описываете задачу — боли, ограничения, цели" },
    { num: "2", title: "Консультация", desc: "Уточняем детали, обсуждаем варианты" },
    { num: "3", title: "Подбор", desc: "Рекомендуем конкретное решение с обоснованием" },
    { num: "4", title: "Обучение", desc: "Показываем технику, даём рекомендации" }
  ];

  const faqs = [
    { q: "Чем вы отличаетесь от обычного интернет-магазина?", a: "Мы не продаём всё подряд. В каталоге только оборудование для восстановления, движения и поддержки тела, рекомендованное нашим специалистом. Перед покупкой консультируем, помогаем понять, подходит ли решение, и обучаем правильному применению." },
    { q: "Только ли для позвоночника ваше оборудование?", a: "Нет. У нас широкий спектр решений: для восстановления после травм и нагрузок, поддержки мышц и тканей, осанки, нейроподдержки, ортопедической коррекции. Позвоночник — одно из важных направлений, но далеко не единственное." },
    { q: "Нужна ли консультация перед покупкой?", a: "Для решений, связанных с реабилитацией и специфическими задачами — настоятельно рекомендуем. Это бесплатно и поможет избежать ошибок в выборе и применении. Для более простых решений консультация опциональна." },
    { q: "Подходит ли оборудование для дома, спорта и клиник?", a: "Да. В каталоге есть решения для домашнего использования, спортсменов и фитнес-центров, а также профессиональное оборудование для клиник и реабилитационных центров." },
    { q: "Работаете ли вы с клиниками и специалистами?", a: "Да, помогаем оснащать реабилитационные кабинеты, спортивные клубы, санатории. Подбираем комплект оборудования и проводим обучение персонала." }
  ];

  return (
    <>
      {/* SEO */}
      <SEO page="home" />
      <FAQSchema faqs={faqs} />
      
      {/* Hero - Premium Dark */}
      <section className="relative min-h-screen bg-[#0B132B] overflow-hidden" data-testid="hero-section">
        {/* Noise texture overlay */}
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`
        }}></div>
        
        {/* Animated gradient orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-[500px] h-[500px] bg-gradient-to-br from-[#0d9488]/15 to-transparent rounded-full blur-[100px] animate-pulse-slow"></div>
          <div className="absolute bottom-20 right-10 w-[400px] h-[400px] bg-gradient-to-br from-[#14b8a6]/12 to-transparent rounded-full blur-[80px] animate-float"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-gradient-radial from-[#1C4E80]/10 to-transparent rounded-full blur-[120px]"></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNNjAgMEgwdjYwaDYwVjB6TTU5IDFIMXY1OGg1OFYxeiIgZmlsbD0icmdiYSgyNTUsMjU1LDI1NSwwLjAyKSIvPjwvZz48L3N2Zz4=')] opacity-50"></div>
        </div>

        <div className="relative z-10 container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl pt-28 md:pt-32 pb-16">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center min-h-[calc(100vh-8rem)]">
            
            {/* Left Content */}
            <div className="text-center lg:text-left">
              {/* Badge */}
              <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full mb-8 animate-fade-in">
                <div className="relative">
                  <div className="w-2.5 h-2.5 bg-[#22c55e] rounded-full"></div>
                  <div className="absolute inset-0 w-2.5 h-2.5 bg-[#22c55e] rounded-full animate-ping opacity-75"></div>
                </div>
                <span className="text-white/80 text-sm font-medium tracking-wide">Экспертная платформа</span>
              </div>

              {/* Headline */}
              <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4rem] font-extrabold text-white leading-[1.05] mb-6 tracking-tight animate-slide-up" style={{ fontFamily: 'Manrope' }}>
                Оборудование для{' '}
                <span className="text-shimmer-glow relative">
                  восстановления
                  <span className="absolute inset-0 blur-xl bg-gradient-to-r from-[#14b8a6]/50 via-[#2dd4bf]/40 to-[#14b8a6]/50 animate-pulse-slow -z-10"></span>
                </span>
                <br className="hidden sm:block" />{' '}
                и поддержки тела
              </h1>

              {/* Subtitle */}
              <p className="text-lg md:text-xl text-[#94a3b8] leading-relaxed mb-10 max-w-xl mx-auto lg:mx-0 animate-slide-up-delayed" style={{ fontFamily: 'IBM Plex Sans' }}>
                Решения для позвоночника, мышц, осанки. Для дома, спорта и клиник. Подбираем под задачу, консультируем, обучаем.
              </p>

              {/* CTA Buttons - Pill shaped */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start mb-12 animate-slide-up-delayed-2">
                <button onClick={() => setShowQuiz(true)} className="group relative px-8 h-14 bg-gradient-to-r from-[#0d9488] to-[#14b8a6] text-white font-bold rounded-full flex items-center justify-center gap-2.5 shadow-lg shadow-[#0d9488]/25 hover:shadow-xl hover:shadow-[#14b8a6]/30 hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-quiz-btn">
                  <HelpCircle className="w-5 h-5" /> Подобрать решение
                </button>
                <button onClick={() => navigate('/contact')} className="px-8 h-14 bg-white/5 backdrop-blur-sm text-white font-semibold rounded-full border border-white/20 flex items-center justify-center gap-2.5 hover:bg-white/10 hover:border-white/30 hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-consultation-btn">
                  Консультация <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              {/* Trust Stats */}
              <div className="flex flex-wrap gap-3 max-w-lg mx-auto lg:mx-0">
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <span className="text-xl font-bold text-[#14b8a6]" style={{ fontFamily: 'IBM Plex Mono' }}>6</span>
                  <span className="text-sm text-[#94a3b8]">направлений</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <span className="text-xl font-bold text-[#14b8a6]" style={{ fontFamily: 'IBM Plex Mono' }}>15+</span>
                  <span className="text-sm text-[#94a3b8]">лет опыта</span>
                </div>
                <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10">
                  <span className="text-xl font-bold text-[#14b8a6]" style={{ fontFamily: 'IBM Plex Mono' }}>К.М.Н.</span>
                  <span className="text-sm text-[#94a3b8]">эксперт</span>
                </div>
              </div>
            </div>

            {/* Right - Image with floating cards */}
            <div className="relative flex items-center justify-center lg:justify-end">
              {/* Main Image Container */}
              <div className="relative">
                {/* Glow effect behind image */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#0d9488]/20 to-[#14b8a6]/15 rounded-3xl blur-2xl"></div>
                
                {/* Image */}
                <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
                  <img 
                    src="/images/og-image.png" 
                    alt="KOSS LAB - технологии здоровья" 
                    className="w-full max-w-lg h-auto object-cover"
                    data-testid="hero-image"
                  />
                  {/* Overlay gradient */}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B132B]/50 via-transparent to-transparent"></div>
                </div>

                {/* Floating Card - Top Right */}
                <div className="absolute -top-8 -right-4 md:top-0 md:-right-16 p-5 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-black/20 animate-float hidden sm:block border border-white/50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#1C4E80] to-[#153A60] rounded-xl flex items-center justify-center shadow-lg">
                      <Shield className="w-6 h-6 text-[#14b8a6]" />
                    </div>
                    <div>
                      <div className="text-[#0f172a] font-bold text-base">Проверено практикой</div>
                      <div className="text-[#64748b] text-sm">Только рабочие решения</div>
                    </div>
                  </div>
                </div>

                {/* Floating Card - Bottom Left */}
                <div className="absolute -bottom-16 -left-4 md:-bottom-12 md:-left-16 p-5 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl shadow-black/20 animate-float-delayed hidden sm:block border border-white/50">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-[#0B132B] to-[#1C4E80] rounded-xl flex items-center justify-center shadow-lg">
                      <Award className="w-6 h-6 text-[#14b8a6]" />
                    </div>
                    <div>
                      <div className="text-[#0f172a] font-bold text-base">Виктор Косс</div>
                      <div className="text-[#64748b] text-sm">Врач-невролог, К.М.Н.</div>
                    </div>
                  </div>
                </div>

                {/* Floating Card - Middle Right (small) */}
                <div className="absolute bottom-1/3 -right-4 md:-right-10 p-4 bg-white/95 backdrop-blur-xl rounded-xl shadow-2xl shadow-black/20 animate-float hidden md:block border border-white/50" style={{ animationDelay: '-2s' }}>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-[#0d9488]" />
                    <span className="text-[#0f172a] font-semibold text-sm">Подбор под задачу</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Scroll indicator */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce-slow">
            <span className="text-[#64748b] text-xs uppercase tracking-widest">Листайте</span>
            <div className="w-6 h-10 rounded-full border-2 border-[#64748b]/30 flex justify-center pt-2">
              <div className="w-1.5 h-3 bg-[#14b8a6] rounded-full animate-scroll-down"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Expert Trust Block */}
      <section className="py-20 md:py-24 relative overflow-hidden" data-testid="expert-section">
        {/* Subtle decorative elements */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fafc]/50 to-transparent"></div>
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gradient-to-br from-[#0d9488]/5 to-transparent rounded-full blur-[100px]"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-br from-[#1C4E80]/5 to-transparent rounded-full blur-[80px]"></div>
        
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl relative z-10">
          {/* Section header */}
          <div className="text-center mb-16 reveal-on-scroll">
            <p className="text-[#0d9488] font-semibold text-sm tracking-widest uppercase mb-4">Почему нам доверяют</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0f172a] tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Экспертный подход
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 stagger-children">
            {expertFeatures.map((item, i) => (
              <div 
                key={i} 
                className="group bg-white rounded-2xl p-10 border border-[#E2E8F0] shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:-translate-y-2 transition-all duration-500 glow-on-hover"
                data-testid={`trust-${i}`}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-[#1C4E80] to-[#0B132B] rounded-2xl flex items-center justify-center mb-8 group-hover:scale-105 transition-transform duration-300">
                  <item.icon className="w-7 h-7 text-[#14b8a6]" />
                </div>
                <h3 className="text-xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                <p className="text-[#64748b] leading-relaxed" style={{ fontFamily: 'IBM Plex Sans' }}>{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* For Whom - Broader Focus */}
      <section className="py-16 md:py-20 relative overflow-hidden" data-testid="audience-section">
        {/* Decorative gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fafc]/50 to-transparent"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-[#0d9488]/5 to-transparent rounded-full blur-[100px]"></div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-16 items-start">
            {/* Sticky left header */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 reveal-on-scroll">
              <p className="text-[#0d9488] font-semibold text-sm tracking-widest uppercase mb-5">Кому подойдёт</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#0f172a] mb-6 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Решения для разных задач
              </h2>
              <p className="text-lg text-[#64748b]">От профилактики до реабилитации, от домашнего использования до профессиональной практики</p>
            </div>
            
            {/* Right column with cards */}
            <div className="lg:col-span-8 space-y-4">
              {audiences.map((item, i) => (
                <div 
                  key={i} 
                  className="p-6 rounded-2xl border border-white/60 bg-white/80 backdrop-blur-sm hover:bg-white hover:border-[#14b8a6]/40 transition-all duration-300 shadow-premium"
                  data-testid={`audience-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#f8fafc] rounded-xl flex items-center justify-center flex-shrink-0 border border-[#e2e8f0]/40">
                      <item.icon className="w-5 h-5 text-[#0f172a]" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0f172a] mb-1" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                      <p className="text-[#64748b] text-sm leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions by Task - Premium Minimal */}
      <section className="py-16 md:py-20 relative overflow-hidden" data-testid="solutions-section">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fafc]/50 to-transparent"></div>
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-[#14b8a6]/4 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl relative z-10">
          {/* Header */}
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-5">Направления</p>
            <h2 className="text-3xl sm:text-4xl md:text-[2.75rem] font-bold text-[#0f172a] mb-5 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Подберём решение под вашу задачу
            </h2>
            <p className="text-lg text-[#64748b] leading-relaxed">
              Выберите направление — поможем подобрать оборудование и проконсультируем по применению
            </p>
          </div>
          
          {/* 4 Premium Cards Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-16">
            {solutions.map((sol, i) => (
              <Link 
                key={i} 
                to={sol.link} 
                className="group animate-card-reveal interactive-card bg-white/80 backdrop-blur-sm rounded-2xl border border-white/70 overflow-hidden shadow-premium"
                style={{ transitionDelay: `${i * 100}ms` }}
                data-testid={`solution-${i}`}
              >
                {/* Subtle Image */}
                <div className="h-40 bg-[#fafbfc] flex items-center justify-center p-6 overflow-hidden">
                  <img 
                    src={sol.img} 
                    alt={sol.title} 
                    className="max-h-full max-w-full object-contain opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-500"
                  />
                </div>
                {/* Content */}
                <div className="p-6 border-t border-[#f1f5f9]">
                  <h3 className="text-lg font-bold text-[#0f172a] mb-2 group-hover:text-[#0d9488] transition-colors" style={{ fontFamily: 'Manrope' }}>
                    {sol.title}
                  </h3>
                  <p className="text-[#64748b] text-sm leading-relaxed">{sol.desc}</p>
                  <div className="mt-4 flex items-center text-[#14b8a6] text-sm font-medium opacity-0 group-hover:opacity-100 translate-x-0 group-hover:translate-x-2 transition-all duration-300">
                    Подробнее <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Secondary CTA Block for Clinics */}
          <div className="bg-gradient-to-br from-[#0f172a] to-[#1e293b] rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#14b8a6]/20 rounded-xl flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-[#14b8a6]" />
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-white" style={{ fontFamily: 'Manrope' }}>
                  Для специалистов и оснащения кабинетов
                </h3>
              </div>
              <p className="text-[#94a3b8] leading-relaxed max-w-xl">
                Помогаем оснащать реабилитационные кабинеты, спортивные клубы и санатории. Подберём комплект оборудования и обучим персонал.
              </p>
            </div>
            <Link to="/b2b" className="btn-teal px-8 h-14 flex items-center gap-2 whitespace-nowrap shadow-premium-lg">
              Узнать условия <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why Trust - Substantial */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="why-trust-section">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#f0fdfa]/50 to-[#f0f9ff]/50"></div>
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-br from-[#14b8a6]/8 to-[#0ea5e9]/8 rounded-full blur-[100px] animate-pulse-slow"></div>
        <div className="absolute bottom-0 left-1/4 w-[400px] h-[400px] bg-gradient-to-tr from-[#0ea5e9]/6 to-transparent rounded-full blur-[80px] animate-float"></div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div className="lg:sticky lg:top-32">
              <p className="text-[#14b8a6] font-semibold text-sm tracking-wide uppercase mb-4">Почему мы</p>
              <h2 className="text-3xl sm:text-4xl md:text-[2.5rem] font-bold text-[#0f172a] mb-6" style={{ fontFamily: 'Manrope' }}>
                Экспертный подход к восстановлению и поддержке тела
              </h2>
              <p className="text-lg text-[#475569] mb-8">
                Не маркетплейс. Это платформа с проверенным оборудованием, экспертной поддержкой и индивидуальным подходом к каждой задаче.
              </p>
              <Link to="/about" className="btn-outline inline-flex items-center gap-2">
                Подробнее о подходе <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            <div className="space-y-6">
              {whyTrust.map((item, i) => (
                <div 
                  key={i} 
                  className="why-trust-card flex gap-6 p-6 rounded-2xl border border-[#e2e8f0] bg-white/80 backdrop-blur-sm cursor-default"
                  style={{ transitionDelay: `${i * 150}ms` }}
                  data-testid={`why-trust-${i}`}
                >
                  <div className="text-3xl font-bold text-[#14b8a6]/30 group-hover:text-[#14b8a6]/50 transition-colors" style={{ fontFamily: 'Manrope' }}>{item.num}</div>
                  <div>
                    <h3 className="font-semibold text-[#0f172a] text-lg mb-2" style={{ fontFamily: 'Manrope' }}>{item.title}</h3>
                    <p className="text-[#475569] leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Section - Viktor Koss */}
      <section className="py-24 md:py-32 bg-[#0f172a]" data-testid="doctor-section">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="lg:sticky lg:top-32 relative">
              <div className="absolute -inset-4 bg-[#14b8a6]/10 rounded-3xl"></div>
              <img 
                src="https://sophiatria.ru/upload/landing/674/t3shkdfhr31ju9ubhuw292m4ps9i6no0/k@1x.jpg" 
                alt="Виктор Викторович Косс — врач-невролог, к.м.н." 
                className="relative z-10 rounded-2xl w-full object-cover" 
                style={{ maxHeight: '520px' }} 
                data-testid="doctor-image" 
              />
            </div>
            
            <div className="text-white space-y-8">
              <div>
                <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-4">Эксперт проекта</p>
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3" style={{ fontFamily: 'Manrope' }}>Виктор Викторович Косс</h2>
                <p className="text-xl text-[#94a3b8]">Врач-невролог, кандидат медицинских наук</p>
              </div>
              
              <blockquote className="text-lg text-[#cbd5e1] leading-relaxed border-l-2 border-[#14b8a6] pl-6">
                «Я специализируюсь на спортивной неврологии и реабилитации. Разрабатываю методики восстановления без хирургического вмешательства и работаю со спортсменами олимпийского уровня. Здесь — только оборудование, которое применяю в собственной практике.»
              </blockquote>
              
              <div className="grid grid-cols-3 gap-4">
                <div className="stat-card bg-white/5 rounded-xl p-4 text-center cursor-default">
                  <div className="text-2xl font-bold text-[#14b8a6]">К.М.Н.</div>
                  <div className="text-[#94a3b8] text-xs mt-1">учёная степень</div>
                </div>
                <div className="stat-card bg-white/5 rounded-xl p-4 text-center cursor-default">
                  <div className="text-2xl font-bold text-[#14b8a6]">Патент</div>
                  <div className="text-[#94a3b8] text-xs mt-1">«Способ Косса»</div>
                </div>
                <div className="stat-card bg-white/5 rounded-xl p-4 text-center cursor-default">
                  <div className="text-2xl font-bold text-[#14b8a6]">НИИ Спорта</div>
                  <div className="text-[#94a3b8] text-xs mt-1">зав. лабораторией</div>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Link to="/contact" className="btn-teal inline-flex items-center gap-2" data-testid="doctor-consultation-btn">
                  Записаться на консультацию <ArrowRight className="w-4 h-4" />
                </Link>
                <a href="https://vkoss.ru" target="_blank" rel="noopener noreferrer" className="btn-outline !border-white/20 !text-white hover:!bg-white/5 inline-flex items-center gap-2">
                  Сайт доктора
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Flagship Products */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="products-section">
        {/* Background decoration */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#f8fafc]/50 to-transparent"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#14b8a6]/4 rounded-full blur-3xl"></div>
        
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl relative z-10">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sticky left column */}
            <div className="lg:w-1/3 lg:flex-shrink-0">
              <div className="lg:sticky lg:top-28">
                <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-4">Основное оборудование</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-[#0f172a] mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                  Рекомендуемые решения
                </h2>
                <p className="text-lg text-[#64748b] mb-6">Оборудование для восстановления, поддержки движения и функционального здоровья — проверено практикой</p>
                <Link to="/catalog" className="btn-navy inline-flex items-center gap-2">
                  Весь каталог <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
            
            {/* Right column with products */}
            <div className="lg:w-2/3">
              <div className="grid grid-cols-2 gap-4 md:gap-6">
                {flagshipProducts.slice(0, 4).map((product) => (
                  <ProductCard key={product.id} product={product} onOpenModal={setSelectedProduct} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How We Help - Process */}
      <section className="py-20 md:py-28 relative overflow-hidden" data-testid="steps-section">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-tl from-[#f0fdfa]/60 via-white to-[#f0f9ff]/40"></div>
        <div className="absolute top-0 left-1/3 w-[600px] h-[600px] bg-gradient-to-br from-[#0ea5e9]/6 to-[#14b8a6]/6 rounded-full blur-[120px] animate-float"></div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="max-w-3xl mx-auto text-center mb-16 reveal-on-scroll">
            <p className="text-[#14b8a6] font-semibold text-sm tracking-wide uppercase mb-4">Как это работает</p>
            <h2 className="text-3xl sm:text-4xl md:text-[2.5rem] font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>
              Процесс подбора решения
            </h2>
          </div>
          
          <div className="relative max-w-5xl mx-auto">
            {/* Connecting line between steps (desktop only) */}
            <div className="hidden md:block absolute top-8 left-[12.5%] right-[12.5%] h-[2px] bg-gradient-to-r from-transparent via-[#14b8a6]/30 to-transparent"></div>
            
            <div className="grid md:grid-cols-4 gap-8">
              {steps.map((step, i) => (
                <div key={i} className="step-card text-center group cursor-default relative" style={{ transitionDelay: `${i * 150}ms` }} data-testid={`step-${i}`}>
                  <div className="step-number w-16 h-16 bg-[#0f172a] rounded-2xl flex items-center justify-center mx-auto mb-5 group-hover:bg-gradient-to-br group-hover:from-[#0f172a] group-hover:to-[#1e293b] relative z-10">
                    <span className="text-2xl font-bold text-[#14b8a6]">{step.num}</span>
                  </div>
                  <h3 className="font-semibold text-[#0f172a] text-lg mb-2 group-hover:text-[#0d9488] transition-colors" style={{ fontFamily: 'Manrope' }}>{step.title}</h3>
                  <p className="text-[#64748b] text-sm">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="faq-section">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-br from-white via-[#f0fdfa]/30 to-[#f0f9ff]/50"></div>
        <div className="absolute bottom-1/4 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-[#14b8a6]/8 to-transparent rounded-full blur-[100px] animate-pulse-slow"></div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            {/* Sticky left column */}
            <div className="lg:col-span-4 lg:sticky lg:top-32">
              <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-4">Вопросы</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0f172a] mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>
                Частые вопросы
              </h2>
              <p className="text-[#64748b] mb-6">Ответы на популярные вопросы о нашем оборудовании и подходе</p>
              <Link to="/contact" className="btn-teal inline-flex items-center gap-2">
                Задать вопрос <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            
            {/* Right column with FAQ */}
            <div className="lg:col-span-8 space-y-4">
              {faqs.map((faq, i) => (
                <Accordion key={i} type="single" collapsible>
                    <AccordionItem 
                      value={`faq-${i}`} 
                      className="border border-[#e2e8f0]/60 rounded-2xl px-6 bg-white/90 backdrop-blur-sm data-[state=open]:border-[#14b8a6]/30 shadow-sm" 
                      data-testid={`faq-item-${i}`}
                    >
                      <AccordionTrigger className="text-left font-semibold text-[#0f172a] hover:no-underline py-5">{faq.q}</AccordionTrigger>
                      <AccordionContent className="text-[#475569] pb-5 leading-relaxed">{faq.a}</AccordionContent>
                    </AccordionItem>
                  </Accordion>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="reviews-section">
        {/* Animated background */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[#f0f9ff]/40 via-white to-[#f0fdfa]/60"></div>
        <div className="absolute top-1/2 left-0 w-[600px] h-[600px] bg-gradient-to-r from-[#14b8a6]/6 to-[#0ea5e9]/6 rounded-full blur-[120px] animate-float"></div>
        <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-gradient-to-tl from-[#0ea5e9]/8 to-transparent rounded-full blur-[80px] animate-pulse-slow"></div>
        
        <div className="relative z-10 container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-5">Отзывы</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0f172a] mb-5 tracking-tight" style={{ fontFamily: 'Manrope' }}>
              Что говорят клиенты
            </h2>
            <p className="text-lg text-[#64748b]">
              Реальные истории людей, которые используют наше оборудование
            </p>
          </div>
          
          {/* CTA to leave review */}
          <div className="flex justify-center mb-12">
            <button 
              onClick={() => setShowReviewForm(true)}
              className="btn-outline inline-flex items-center gap-2"
            >
              <Star className="w-4 h-4" /> Оставить отзыв
            </button>
          </div>

          {/* Reviews from database - featured first */}
          {(() => {
            const featuredReviews = approvedReviews.filter(r => r.is_featured);
            const regularReviews = approvedReviews.filter(r => !r.is_featured);
            const sortedReviews = [...featuredReviews, ...regularReviews];
            const displayReviews = showAllReviews ? sortedReviews : sortedReviews.slice(0, 6);
            
            return (
              <>
                <div className="grid md:grid-cols-3 gap-6">
                  {displayReviews.map((review, i) => (
                    <ReviewCard key={review.id || i} review={review} />
                  ))}
                </div>
                
                {sortedReviews.length > 6 && (
                  <div className="text-center mt-8">
                    <button 
                      onClick={() => setShowAllReviews(!showAllReviews)}
                      className="btn-outline inline-flex items-center gap-2"
                    >
                      {showAllReviews ? (
                        <>Свернуть</>
                      ) : (
                        <>Показать ещё {sortedReviews.length - 6} отзывов</>
                      )}
                    </button>
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-28 bg-[#0f172a]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-8 max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Manrope' }}>
            Не знаете, с чего начать?
          </h2>
          <p className="text-xl text-[#94a3b8] mb-10 max-w-2xl mx-auto leading-relaxed">
            Пройдите короткий опрос — подберём решение под вашу задачу. Или свяжитесь с нами напрямую.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => setShowQuiz(true)} className="btn-teal inline-flex items-center justify-center gap-2 px-8 h-14" data-testid="cta-quiz">
              <HelpCircle className="w-5 h-5" /> Подобрать решение
            </button>
            <a href="tel:+79265995252" className="btn-outline !border-white/20 !text-white hover:!bg-white/5 inline-flex items-center justify-center gap-2 px-8 h-14">
              <Phone className="w-4 h-4" /> +7 (926) 599-52-52
            </a>
          </div>
        </div>
      </section>

      {/* Quiz Modal */}
      <QuizModal isOpen={showQuiz} onClose={() => setShowQuiz(false)} />
      
      {/* Review Form Modal */}
      <ReviewForm isOpen={showReviewForm} onClose={() => setShowReviewForm(false)} />
      
      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct} 
        isOpen={!!selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
      />
      
      {/* Payment Success Modal */}
      <Dialog open={showPaymentSuccess} onOpenChange={setShowPaymentSuccess}>
        <DialogContent className="max-w-md">
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-[#22c55e]/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-[#22c55e]" />
            </div>
            <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>
              Оплата прошла успешно!
            </h2>
            <p className="text-[#64748b] mb-6">
              Спасибо за ваш заказ. Мы уже начали его обработку.
            </p>
            
            <div className="bg-[#f8fafc] rounded-xl p-4 text-left mb-6">
              <p className="text-sm text-[#64748b] mb-2">
                <strong className="text-[#0f172a]">Номер заказа:</strong>
              </p>
              <p className="font-mono text-sm text-[#14b8a6] break-all">{paymentOrderId}</p>
            </div>
            
            {deliveryType === 'pickup' ? (
              /* Самовывоз */
              <div className="bg-[#f0fdfa] border border-[#14b8a6]/20 rounded-xl p-4 text-left mb-6">
                <h3 className="font-semibold text-[#0f172a] mb-3 flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#14b8a6]" />
                  Самовывоз
                </h3>
                <div className="space-y-3 text-sm">
                  <div>
                    <p className="font-medium text-[#0f172a]">Адрес клиники:</p>
                    <p className="text-[#475569]">г. Москва, ул. Лужники, 24, стр. 24</p>
                    <p className="text-[#475569]">Клиника доктора Косса</p>
                  </div>
                  <div>
                    <p className="font-medium text-[#0f172a]">Часы работы:</p>
                    <p className="text-[#475569]">Пн-Пт: 10:00 — 19:00</p>
                    <p className="text-[#475569]">Сб: 11:00 — 17:00</p>
                    <p className="text-[#475569]">Вс: выходной</p>
                  </div>
                  <div className="pt-2 border-t border-[#14b8a6]/10">
                    <p className="text-[#64748b]">Мы свяжемся с вами, когда заказ будет готов к выдаче</p>
                  </div>
                </div>
              </div>
            ) : (
              /* Доставка */
              <div className="bg-[#f0fdfa] border border-[#14b8a6]/20 rounded-xl p-4 text-left mb-6">
                <h3 className="font-semibold text-[#0f172a] mb-2">Что дальше?</h3>
                <ul className="text-sm text-[#475569] space-y-2">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#14b8a6] mt-0.5 flex-shrink-0" />
                    <span>Чек отправлен на вашу почту</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#14b8a6] mt-0.5 flex-shrink-0" />
                    <span>Мы свяжемся с вами для уточнения деталей доставки</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#14b8a6] mt-0.5 flex-shrink-0" />
                    <span>Доставка обычно занимает 2-5 рабочих дней</span>
                  </li>
                </ul>
              </div>
            )}
            
            <p className="text-sm text-[#64748b] mb-4">
              Есть вопросы? Свяжитесь с нами:
            </p>
            <div className="flex justify-center gap-4 mb-4">
              <a href="tel:+79265995252" className="flex items-center gap-2 text-[#14b8a6] hover:underline">
                <Phone className="w-4 h-4" />
                <span className="text-sm">Позвонить</span>
              </a>
              <a href="mailto:info@kosslab.ru" className="flex items-center gap-2 text-[#14b8a6] hover:underline">
                <Mail className="w-4 h-4" />
                <span className="text-sm">Написать</span>
              </a>
            </div>
            
            {/* Assistant hint */}
            <div className="bg-gradient-to-r from-[#0B132B] to-[#1C4E80] rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-[#0d9488] to-[#14b8a6] rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0">
                  А
                </div>
                <div className="text-left">
                  <p className="text-white text-sm font-medium">Нужна помощь с заказом?</p>
                  <p className="text-[#94a3b8] text-xs">Спросите у Анны — нашего консультанта</p>
                </div>
              </div>
              <button 
                onClick={() => {
                  setShowPaymentSuccess(false);
                  // Trigger chat widget open via custom event
                  window.dispatchEvent(new CustomEvent('openChatWidget'));
                }}
                className="w-full mt-3 py-2 px-4 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Написать Анне
              </button>
            </div>
            
            <button 
              onClick={() => setShowPaymentSuccess(false)}
              className="btn-teal w-full h-12"
            >
              Отлично, понятно!
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

// ============ PRODUCT MODAL ============
const ProductModal = ({ product, isOpen, onClose }) => {
  const { addToCart } = useCart();
  
  if (!product) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto p-0">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Image */}
          <div className="bg-[#f8fafc] p-8 flex items-center justify-center">
            <img 
              src={product.image_url} 
              alt={product.name}
              className="max-h-[400px] w-auto object-contain"
            />
          </div>
          
          {/* Content */}
          <div className="p-8 space-y-6">
            <div>
              <span className="inline-block px-3 py-1 bg-[#f1f5f9] text-[#475569] text-xs font-medium rounded-full mb-3">
                {product.category_name}
              </span>
              <h2 className="text-2xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>
                {product.name}
              </h2>
              <p className="text-[#64748b] leading-relaxed">{product.tagline}</p>
            </div>
            
            <div className="text-3xl font-bold text-[#0f172a]">
              {product.price.toLocaleString()} ₽
            </div>
            
            {product.full_description && (
              <div>
                <p className="text-[#475569] text-sm leading-relaxed">{product.full_description}</p>
              </div>
            )}
            
            {product.features && product.features.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#0f172a] mb-3 text-sm" style={{ fontFamily: 'Manrope' }}>Особенности</h4>
                <ul className="space-y-2">
                  {product.features.slice(0, 4).map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#475569]">
                      <Check className="w-4 h-4 text-[#14b8a6] mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {product.target_audience && product.target_audience.length > 0 && (
              <div>
                <h4 className="font-semibold text-[#0f172a] mb-2 text-sm" style={{ fontFamily: 'Manrope' }}>Кому подойдёт</h4>
                <div className="flex flex-wrap gap-2">
                  {product.target_audience.slice(0, 4).map((audience, i) => (
                    <span key={i} className="px-3 py-1 bg-[#f1f5f9] text-[#475569] text-xs rounded-full">
                      {audience}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="flex gap-3 pt-4 border-t border-[#e2e8f0]">
              <button 
                onClick={() => { addToCart(product); onClose(); }}
                className="btn-teal flex-1 h-12 flex items-center justify-center gap-2"
                data-testid="modal-add-to-cart"
              >
                <ShoppingCart className="w-4 h-4" /> В корзину
              </button>
              <Link 
                to={`/product/${product.slug}`}
                onClick={onClose}
                className="btn-outline h-12 px-6 flex items-center justify-center gap-2"
              >
                Подробнее
              </Link>
            </div>
            
            {product.consultation_needed && (
              <p className="text-xs text-[#94a3b8] text-center">
                {product.consultation_needed}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ PRODUCT CARD ============
const ProductCard = ({ product, onOpenModal }) => {
  const { addToCart, cartItems, updateQuantity, removeFromCart } = useCart();
  
  const cartItem = cartItems.find(item => item.product.id === product.id);
  const quantity = cartItem ? cartItem.quantity : 0;
  const inStock = product.in_stock !== false; // по умолчанию true если не указано

  return (
    <div 
      className="group bg-white rounded-2xl border border-[#E2E8F0] overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer" 
      data-testid={`product-${product.id}`}
      onClick={() => onOpenModal(product)}
    >
      <div className="overflow-hidden aspect-square bg-[#F8FAFC] relative">
        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        {/* Индикатор наличия */}
        <div className={`absolute top-3 right-3 px-2 py-1 rounded-full text-[10px] font-medium ${inStock ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {inStock ? 'В наличии' : 'Под заказ'}
        </div>
      </div>
      <div className="p-4 sm:p-6 space-y-2 sm:space-y-3">
        <span className="inline-block px-2 sm:px-3 py-1 sm:py-1.5 bg-[#f0fdfa] text-[#0d9488] text-[10px] sm:text-xs font-medium rounded-full">{product.category_name}</span>
        <h3 className="font-bold text-sm sm:text-base text-[#0f172a] group-hover:text-[#0d9488] transition-colors leading-snug line-clamp-2" style={{ fontFamily: 'Manrope' }}>{product.name}</h3>
        <p className="text-[#64748b] text-xs sm:text-sm line-clamp-2 leading-relaxed hidden sm:block" style={{ fontFamily: 'IBM Plex Sans' }}>{product.tagline}</p>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0 pt-3 sm:pt-4 border-t border-[#F1F5F9]">
          <span className="text-lg sm:text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'IBM Plex Mono' }}>{product.price.toLocaleString()} ₽</span>
          
          {quantity > 0 ? (
            /* Товар в корзине — показываем счётчик */
            <div className="flex items-center gap-1 sm:gap-2" onClick={(e) => e.stopPropagation()}>
              <button 
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-[#f1f5f9] hover:bg-[#e2e8f0] text-[#0f172a] rounded-full transition-colors"
                onClick={() => updateQuantity(product.id, quantity - 1)}
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-8 text-center font-semibold text-[#0f172a]">{quantity}</span>
              <button 
                className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center bg-[#0d9488] hover:bg-[#0f766e] text-white rounded-full transition-colors"
                onClick={() => addToCart(product)}
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          ) : (
            /* Товар не в корзине — кнопка добавления */
            <button 
              className="py-2 sm:py-2.5 px-4 sm:px-5 bg-[#0B132B] text-white text-xs sm:text-sm font-semibold rounded-full hover:bg-[#1C4E80] transition-colors w-full sm:w-auto" 
              onClick={(e) => { e.stopPropagation(); addToCart(product); }} 
              data-testid={`add-to-cart-${product.id}`}
            >
              В корзину
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ============ CATALOG PAGE ============
const CatalogPage = () => {
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cat = params.get('category');
    if (cat) setSelectedCategory(cat);

    const fetchData = async () => {
      try {
        const [catRes, prodRes] = await Promise.all([axios.get(`${API}/categories`), axios.get(`${API}/products`)]);
        setCategories(catRes.data);
        setProducts(prodRes.data);
      } catch (error) { console.error('Error:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const filteredProducts = selectedCategory ? products.filter(p => p.category_id === selectedCategory) : products;
  const selectedCategoryData = categories.find(c => c.id === selectedCategory);

  return (
    <>
      <SEO page="catalog" category={selectedCategoryData} />
      <div className="pt-32 pb-20" data-testid="catalog-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
        <div className="mb-14">
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4 tracking-tight" style={{ fontFamily: 'Manrope' }}>Каталог решений</h1>
          <p className="text-lg text-[#64748b]">Оборудование для восстановления, движения, поддержки тела и функционального здоровья</p>
        </div>
        <div className="flex gap-3 mb-12 overflow-x-auto pb-2 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0 md:flex-wrap">
          <button onClick={() => setSelectedCategory('')} className={`px-5 py-2.5 rounded-full font-medium transition-all text-sm whitespace-nowrap flex-shrink-0 ${!selectedCategory ? 'bg-[#0B132B] text-white' : 'bg-white text-[#475569] hover:bg-[#f1f5f9] border border-[#e2e8f0]'}`} data-testid="filter-all">Все решения</button>
          {categories.map(cat => (
            <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`px-5 py-2.5 rounded-full font-medium transition-all text-sm whitespace-nowrap flex-shrink-0 ${selectedCategory === cat.id ? 'bg-[#0B132B] text-white' : 'bg-white text-[#475569] hover:bg-[#f1f5f9] border border-[#e2e8f0]'}`} data-testid={`filter-${cat.id}`}>{cat.name}</button>
          ))}
        </div>
        {loading ? (
          <div className="flex justify-center py-20"><div className="w-12 h-12 border-4 border-[#e2e8f0] border-t-[#0d9488] rounded-full animate-spin"></div></div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredProducts.map(product => (
              <ProductCard key={product.id} product={product} onOpenModal={setSelectedProduct} />
            ))}
          </div>
        )}
        {!loading && filteredProducts.length === 0 && <div className="text-center py-20"><p className="text-[#64748b] text-lg">В этой категории пока нет решений</p></div>}
      </div>
      
      {/* Product Modal */}
      <ProductModal 
        product={selectedProduct} 
        isOpen={!!selectedProduct} 
        onClose={() => setSelectedProduct(null)} 
      />
    </div>
    </>
  );
};

// ============ PRODUCT PAGE ============
const ProductPage = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderForm, setOrderForm] = useState({ name: '', phone: '', email: '', notes: '' });
  const [orderLoading, setOrderLoading] = useState(false);
  const { addToCart } = useCart();

  useEffect(() => {
    const fetchProduct = async () => {
      try { const res = await axios.get(`${API}/products/${slug}`); setProduct(res.data); }
      catch (error) { console.error('Error:', error); }
      finally { setLoading(false); }
    };
    fetchProduct();
  }, [slug]);

  const handleOrder = async (e) => {
    e.preventDefault();
    if (!orderForm.name.trim() || !orderForm.phone.trim()) {
      toast.error('Заполните имя и телефон');
      return;
    }
    if (!orderForm.email.trim()) {
      toast.error('Email обязателен для оплаты');
      return;
    }
    setOrderLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, {
        customer_name: orderForm.name,
        customer_phone: orderForm.phone,
        customer_email: orderForm.email,
        product_id: product.id,
        product_name: product.name,
        product_price: product.price,
        quantity: 1,
        notes: orderForm.notes || null
      });
      
      console.log('Order response:', response.data);
      
      if (response.data.payment_url) {
        toast.success('Перенаправляем на оплату...');
        setTimeout(() => {
          window.location.href = response.data.payment_url;
        }, 500);
      } else if (response.data.success) {
        toast.success('Заказ оформлен!');
        setShowOrderModal(false);
        setOrderForm({ name: '', phone: '', email: '', notes: '' });
      } else {
        toast.error(response.data.message || 'Ошибка при оформлении заказа');
      }
    } catch (error) {
      console.error('Order error:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Ошибка при оформлении заказа';
      toast.error(errorMsg);
    } finally {
      setOrderLoading(false);
    }
  };

  if (loading) return <div className="pt-28 pb-20 flex justify-center"><div className="w-12 h-12 border-4 border-[#e2e8f0] border-t-[#14b8a6] rounded-full animate-spin"></div></div>;
  if (!product) return <div className="pt-28 pb-20 text-center"><h1 className="text-2xl text-[#64748b]">Решение не найдено</h1></div>;

  return (
    <>
      <SEO page="product" product={product} />
      <ProductSchema product={product} />
      <BreadcrumbSchema items={[
        { name: 'Главная', url: 'https://kosslab.ru/' },
        { name: 'Каталог', url: 'https://kosslab.ru/catalog' },
        { name: product.name, url: `https://kosslab.ru/product/${product.slug || product.id}` }
      ]} />
      <div className="pt-28 pb-20" data-testid="product-page">
      {/* Order Modal */}
      {showOrderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowOrderModal(false)}>
          <div className="bg-white rounded-2xl p-8 max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>Оформить заказ</h3>
              <button onClick={() => setShowOrderModal(false)} className="p-2 hover:bg-[#f1f5f9] rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="bg-[#f8fafc] rounded-xl p-4 mb-6">
              <p className="font-semibold text-[#0f172a]">{product.name}</p>
              <p className="text-xl font-bold text-[#14b8a6]">{product.price.toLocaleString()} ₽</p>
            </div>
            <form onSubmit={handleOrder} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Ваше имя *</label>
                <input
                  type="text"
                  value={orderForm.name}
                  onChange={e => setOrderForm({...orderForm, name: e.target.value})}
                  className="input-premium"
                  placeholder="Иван Иванов"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Телефон *</label>
                <input
                  type="tel"
                  value={orderForm.phone}
                  onChange={e => setOrderForm({...orderForm, phone: e.target.value})}
                  className="input-premium"
                  placeholder="+7 (999) 123-45-67"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Email *</label>
                <input
                  type="email"
                  value={orderForm.email}
                  onChange={e => setOrderForm({...orderForm, email: e.target.value})}
                  className="input-premium"
                  placeholder="email@example.com"
                  required
                />
                <p className="text-xs text-[#64748b] mt-1">Для отправки чека об оплате</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1">Комментарий к заказу</label>
                <textarea
                  value={orderForm.notes}
                  onChange={e => setOrderForm({...orderForm, notes: e.target.value})}
                  className="input-premium min-h-[80px]"
                  placeholder="Вопросы, пожелания..."
                />
              </div>
              
              {/* Payment info */}
              <div className="bg-[#f0fdfa] border border-[#14b8a6]/20 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-medium text-[#0f172a]">Оплата онлайн</span>
                  <span className="text-[10px] bg-[#FFDD2D] text-black px-2 py-0.5 rounded font-medium">T-Bank</span>
                </div>
                <p className="text-xs text-[#64748b]">Безопасная оплата банковской картой</p>
              </div>
              
              <div className="flex items-start gap-2 text-xs text-[#64748b]">
                <input type="checkbox" required className="mt-1" />
                <span>Нажимая кнопку, вы соглашаетесь с <Link to="/privacy" className="text-[#14b8a6] hover:underline">политикой конфиденциальности</Link> и <Link to="/offer" className="text-[#14b8a6] hover:underline">условиями оферты</Link></span>
              </div>
              <button type="submit" disabled={orderLoading} className="btn-teal w-full h-12">
                {orderLoading ? 'Оформление...' : 'Перейти к оплате'}
              </button>
            </form>
          </div>
        </div>
      )}
      
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center gap-2 text-sm text-[#64748b] mb-8">
          <Link to="/" className="hover:text-[#0f172a]">Главная</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to="/catalog" className="hover:text-[#0f172a]">Каталог</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-[#0f172a]">{product.name}</span>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div><img src={product.image_url} alt={product.name} className="w-full rounded-3xl" data-testid="product-image" /></div>
          <div className="space-y-6">
            <span className="badge-navy">{product.category_name}</span>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }} data-testid="product-title">{product.name}</h1>
            <p className="text-lg text-[#0d9488] font-medium">{product.tagline}</p>
            <p className="text-[#475569] leading-relaxed">{product.short_description}</p>
            <div className="flex items-baseline gap-4 py-4 border-y border-[#f1f5f9]">
              <span className="text-4xl font-bold text-[#0f172a]">{product.price.toLocaleString()} ₽</span>
            </div>
            <div className="flex gap-4">
              <button className="btn-teal flex-1 h-14 flex items-center justify-center gap-2" onClick={() => setShowOrderModal(true)} data-testid="order-btn"><ShoppingCart className="w-5 h-5" />Заказать</button>
              <Link to="/contact" className="btn-outline h-14 flex items-center justify-center gap-2 px-6"><MessageCircle className="w-5 h-5" />Консультация</Link>
            </div>
            <div className="bg-[#f8fafc] rounded-2xl p-5">
              <p className="text-sm text-[#475569] flex items-start gap-3">
                <Shield className="w-5 h-5 text-[#0d9488] flex-shrink-0 mt-0.5" />
                <span>{product.consultation_needed}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="grid lg:grid-cols-3 gap-6 mb-12">
          <div className="card-premium p-7">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Для кого подходит</h3>
            <ul className="space-y-3">
              {product.target_audience.map((item, i) => <li key={i} className="feature-check"><CheckCircle className="w-5 h-5 text-[#14b8a6]" /><span>{item}</span></li>)}
            </ul>
          </div>
          <div className="card-premium p-7">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Какие задачи помогает решать</h3>
            <ul className="space-y-3">
              {product.problems_solved.map((item, i) => <li key={i} className="feature-check"><Target className="w-5 h-5 text-[#14b8a6]" /><span>{item}</span></li>)}
            </ul>
          </div>
          <div className="card-premium p-7">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Особенности</h3>
            <ul className="space-y-3">
              {product.features.map((item, i) => <li key={i} className="feature-check"><Check className="w-5 h-5 text-[#14b8a6]" /><span>{item}</span></li>)}
            </ul>
          </div>
        </div>
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="card-premium p-7">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Как работает</h3>
            <p className="text-[#475569] leading-relaxed">{product.how_it_works}</p>
          </div>
          <div className="card-premium p-7">
            <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Формат применения</h3>
            <p className="text-[#475569] leading-relaxed">{product.usage_format}</p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// ============ ABOUT PAGE ============
const AboutPage = () => {
  return (
    <>
      <SEO page="about" />
      <div className="pt-32 pb-20" data-testid="about-page">
      <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
        <div className="text-center mb-20">
          <p className="text-[#0d9488] font-medium text-sm tracking-widest uppercase mb-4">Об эксперте</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-5" style={{ fontFamily: 'Manrope' }}>Виктор Викторович Косс</h1>
          <p className="text-xl text-[#64748b] max-w-3xl mx-auto">Врач-невролог, кандидат медицинских наук, специалист по спортивной неврологии и реабилитации</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-16 items-start mb-20">
          <div className="sticky top-32">
            <img src="https://sophiatria.ru/upload/landing/674/t3shkdfhr31ju9ubhuw292m4ps9i6no0/k@1x.jpg" alt="Виктор Викторович Косс" className="rounded-2xl shadow-premium-xl w-full" />
          </div>
          <div className="space-y-8">
            <div>
              <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-3">Специализация</p>
              <p className="text-[#475569] leading-relaxed text-lg">Спортивная медицина, неврология, мануальная терапия, рефлексотерапия. Заведующий лабораторией физиологии спорта и реабилитации НИИ Спорта РГУФК.</p>
            </div>
            <div>
              <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-3">Направления работы</p>
              <ul className="space-y-3 text-[#475569]">
                <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#14b8a6] mt-0.5 flex-shrink-0" />Разработка методов реабилитации и лечения спортивных травм без хирургического вмешательства</li>
                <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#14b8a6] mt-0.5 flex-shrink-0" />Улучшение профессиональных спортивных результатов методами биологической обратной связи (БОС)</li>
                <li className="flex items-start gap-3"><CheckCircle className="w-5 h-5 text-[#14b8a6] mt-0.5 flex-shrink-0" />Лечение межпозвонковой грыжи шейного и поясничного отдела позвоночника</li>
              </ul>
            </div>
            <div className="bg-[#f8fafc] rounded-2xl p-6 border border-[#e2e8f0]">
              <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-3">Запатентованная методика</p>
              <h3 className="text-xl font-bold text-[#0f172a] mb-2" style={{ fontFamily: 'Manrope' }}>«Способ Косса»</h3>
              <p className="text-[#475569]">Авторский патент на лечение межпозвонковой грыжи безоперационным методом</p>
            </div>
            <div>
              <p className="text-[#14b8a6] font-semibold text-sm tracking-widest uppercase mb-3">Работа со спортсменами</p>
              <p className="text-[#475569] leading-relaxed">Более 8 лет разрабатывает тренинг-программы для спортсменов олимпийского уровня в следующих видах спорта: биатлон, вольная борьба, дзюдо, самбо, стрельба из лука и художественная гимнастика.</p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/publications" className="btn-teal inline-flex items-center gap-2">
                Публикации <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="https://vkoss.ru" target="_blank" rel="noopener noreferrer" className="btn-navy inline-flex items-center gap-2">
                Сайт доктора <ArrowRight className="w-4 h-4" />
              </a>
              <a href="https://sophiatria.ru/kossvv/" target="_blank" rel="noopener noreferrer" className="btn-outline inline-flex items-center gap-2">
                Клиника «Софиатрия»
              </a>
            </div>
          </div>
        </div>
        <div className="bg-[#0f172a] rounded-2xl p-8 md:p-12">
          <h2 className="text-2xl font-bold text-white mb-8 text-center" style={{ fontFamily: 'Manrope' }}>Клиники доктора Косса в Москве</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>Институт спортивной неврологии</h3>
              <p className="text-[#94a3b8] mb-3">Лаборатория спортивной неврологии</p>
              <p className="text-[#cbd5e1] text-sm mb-2">ул. Лужники, 24, стр. 24, Москва</p>
              <p className="text-[#14b8a6] font-medium">+7 (926) 599-52-52</p>
            </div>
            <div className="bg-white/5 rounded-xl p-6">
              <h3 className="text-lg font-bold text-white mb-3" style={{ fontFamily: 'Manrope' }}>Клиника «Софиатрия»</h3>
              <p className="text-[#94a3b8] mb-3">Научно-медицинский центр</p>
              <p className="text-[#cbd5e1] text-sm mb-2">Шубинский пер., 6, стр. 1, Москва</p>
              <p className="text-[#14b8a6] font-medium">+7 (985) 928-77-28</p>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

// ============ PUBLICATIONS PAGE ============
const PublicationsPage = () => {
  const scientificArticles = [
    {
      title: "Неслучайные травмы в спорте: нейрофизиологические и биомеханические аспекты",
      authors: "Косс В.В., Винер И.А. и др.",
      journal: "Вестник МГПУ, серия «Естественные науки»",
      year: "2026",
      description: "Исследование взаимосвязи нейрофизиологических особенностей и биомеханических паттернов движения у профессиональных спортсменов. Разработана комплексная методика нейробиомеханической диагностики функциональных ортопедо-неврологических нарушений.",
      link: "https://cyberleninka.ru/article/n/nesluchaynye-travmy-v-sporte-neyrofiziologicheskie-i-biomehanicheskie-aspekty",
      type: "article"
    },
    {
      title: "Способ лечения межпозвонковой грыжи",
      authors: "Косс В.В.",
      journal: "Патент РФ № RU2493889C1",
      year: "2013",
      description: "Авторский патент на безоперационное лечение межпозвонковой грыжи методом инъекционной рассасывающей терапии с применением препаратов, обладающих ферментативными свойствами.",
      link: "https://patents.google.com/patent/RU2493889C1/ru",
      type: "patent"
    }
  ];

  const mediaArticles = [
    {
      title: "Как избавиться от боли в спине без операции",
      source: "Комсомольская Правда",
      year: "2017",
      description: "Интервью о современных методах лечения межпозвонковых грыж и спортивных травм позвоночника без хирургического вмешательства.",
      link: "https://www.kp.ru/daily/26646/3666605/",
      type: "interview"
    },
    {
      title: "Спортивные травмы: профилактика и лечение",
      source: "Комсомольская Правда",
      year: "2017",
      description: "Экспертное мнение о причинах травматизма в профессиональном спорте и методах реабилитации спортсменов.",
      link: "https://www.kp.ru/daily/26704/3729185/",
      type: "interview"
    },
    {
      title: "Грыжа межпозвоночного диска: новые методы лечения",
      source: "Российская Газета",
      year: "2013",
      description: "Подробный материал о инновационных безоперационных методах лечения заболеваний позвоночника.",
      link: "https://rg.ru/2013/06/04/koss.html",
      type: "interview"
    },
    {
      title: "Как побороть предновогодний стресс",
      source: "Аргументы и Факты",
      year: "2024",
      description: "Советы невролога о влиянии стресса на организм и методах его преодоления.",
      link: "https://aif.ru/health/nevrolog-koss-obyasnil-kak-poborot-prednovogodniy-stress",
      type: "interview"
    }
  ];

  const getTypeIcon = (type) => {
    switch(type) {
      case 'article': return <FileText className="w-5 h-5" />;
      case 'patent': return <Award className="w-5 h-5" />;
      case 'interview': return <Newspaper className="w-5 h-5" />;
      default: return <FileText className="w-5 h-5" />;
    }
  };

  const getTypeLabel = (type) => {
    switch(type) {
      case 'article': return 'Научная статья';
      case 'patent': return 'Патент';
      case 'interview': return 'Интервью';
      default: return 'Публикация';
    }
  };

  return (
    <>
      <SEO 
        title="Публикации | Виктор Косс | KOSS LAB"
        description="Научные статьи, патенты и интервью доктора Виктора Косса — врача-невролога, специалиста по спортивной медицине и реабилитации"
      />
      <div className="pt-32 pb-20" data-testid="publications-page">
        <div className="container mx-auto px-4 md:px-8 lg:px-12 max-w-7xl">
          {/* Header */}
          <div className="text-center mb-16">
            <p className="text-[#0d9488] font-medium text-sm tracking-widest uppercase mb-4">Научная деятельность</p>
            <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-5" style={{ fontFamily: 'Manrope' }}>Публикации</h1>
            <p className="text-xl text-[#64748b] max-w-3xl mx-auto">Научные работы, патенты и экспертные материалы доктора Виктора Косса</p>
          </div>

          {/* Author card */}
          <div className="bg-gradient-to-r from-[#0B132B] to-[#1C4E80] rounded-2xl p-6 md:p-8 mb-16">
            <div className="flex flex-col md:flex-row items-center gap-6">
              <img 
                src="https://sophiatria.ru/upload/landing/674/t3shkdfhr31ju9ubhuw292m4ps9i6no0/k@1x.jpg" 
                alt="Виктор Викторович Косс" 
                className="w-24 h-24 md:w-32 md:h-32 rounded-full object-cover border-4 border-white/20"
              />
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Manrope' }}>Виктор Викторович Косс</h2>
                <p className="text-[#94a3b8] mb-3">К.М.Н., врач-невролог, заведующий лабораторией спортивной неврологии НИИ Спорта РГУФКСМиТ</p>
                <div className="flex flex-wrap justify-center md:justify-start gap-2">
                  <span className="px-3 py-1 bg-white/10 text-white text-xs rounded-full">Член-корр. Американской академии неврологии</span>
                  <span className="px-3 py-1 bg-white/10 text-white text-xs rounded-full">Автор патента на лечение грыж</span>
                </div>
              </div>
              <div className="md:ml-auto">
                <a href="/about" className="btn-teal inline-flex items-center gap-2">
                  Об эксперте <ArrowRight className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Scientific Articles */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#f0fdfa] rounded-xl flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#0d9488]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>Научные работы и патенты</h2>
            </div>
            <div className="space-y-6">
              {scientificArticles.map((article, idx) => (
                <a 
                  key={idx}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-2xl border border-[#e2e8f0] p-6 hover:shadow-lg hover:border-[#14b8a6]/30 transition-all group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${article.type === 'patent' ? 'bg-amber-50 text-amber-600' : 'bg-[#f0fdfa] text-[#0d9488]'}`}>
                      {getTypeIcon(article.type)}
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <span className={`text-xs font-medium px-2 py-1 rounded-full ${article.type === 'patent' ? 'bg-amber-100 text-amber-700' : 'bg-[#f0fdfa] text-[#0d9488]'}`}>
                          {getTypeLabel(article.type)}
                        </span>
                        <span className="text-xs text-[#94a3b8]">{article.year}</span>
                      </div>
                      <h3 className="text-lg font-bold text-[#0f172a] mb-2 group-hover:text-[#0d9488] transition-colors" style={{ fontFamily: 'Manrope' }}>
                        {article.title}
                      </h3>
                      <p className="text-sm text-[#64748b] mb-2">{article.authors} — {article.journal}</p>
                      <p className="text-[#475569] text-sm leading-relaxed">{article.description}</p>
                      <div className="mt-4 flex items-center gap-2 text-[#0d9488] text-sm font-medium">
                        Читать полностью <ExternalLink className="w-4 h-4" />
                      </div>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </section>

          {/* Media Articles */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#f0fdfa] rounded-xl flex items-center justify-center">
                <Newspaper className="w-5 h-5 text-[#0d9488]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>Интервью и статьи в СМИ</h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {mediaArticles.map((article, idx) => (
                <a 
                  key={idx}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-white rounded-2xl border border-[#e2e8f0] p-6 hover:shadow-lg hover:border-[#14b8a6]/30 transition-all group"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-blue-600">
                      {article.source}
                    </span>
                    <span className="text-xs text-[#94a3b8]">{article.year}</span>
                  </div>
                  <h3 className="text-lg font-bold text-[#0f172a] mb-2 group-hover:text-[#0d9488] transition-colors" style={{ fontFamily: 'Manrope' }}>
                    {article.title}
                  </h3>
                  <p className="text-[#475569] text-sm leading-relaxed mb-4">{article.description}</p>
                  <div className="flex items-center gap-2 text-[#0d9488] text-sm font-medium">
                    Читать <ExternalLink className="w-4 h-4" />
                  </div>
                </a>
              ))}
            </div>
          </section>

          {/* Certificates & License */}
          <section className="mb-16">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-10 h-10 bg-[#f0fdfa] rounded-xl flex items-center justify-center">
                <Award className="w-5 h-5 text-[#0d9488]" />
              </div>
              <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>Квалификация и лицензии</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Сертификаты */}
              <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
                <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Сертификаты специалиста</h3>
                <div className="space-y-3">
                  {[
                    { name: "Неврология", desc: "Диагностика и лечение заболеваний нервной системы" },
                    { name: "Мануальная терапия", desc: "Лечение опорно-двигательного аппарата" },
                    { name: "Рефлексотерапия", desc: "Методы воздействия на рефлексогенные зоны" },
                    { name: "Спортивная медицина", desc: "Реабилитация и профилактика спортивных травм" }
                  ].map((cert, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 bg-[#f8fafc] rounded-xl">
                      <div className="w-8 h-8 bg-[#0d9488] rounded-lg flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <p className="font-semibold text-[#0f172a] text-sm">{cert.name}</p>
                        <p className="text-xs text-[#64748b]">{cert.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Лицензия и образование */}
              <div className="space-y-6">
                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
                  <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Лицензия клиники</h3>
                  <div className="flex items-start gap-3 p-3 bg-[#f8fafc] rounded-xl">
                    <div className="w-8 h-8 bg-[#0B132B] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0f172a] text-sm">№ ЛО-77-01-013411</p>
                      <p className="text-xs text-[#64748b]">Клиника доктора Косса, г. Москва</p>
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl border border-[#e2e8f0] p-6">
                  <h3 className="text-lg font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Научная степень</h3>
                  <div className="flex items-start gap-3 p-3 bg-[#f8fafc] rounded-xl">
                    <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Award className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#0f172a] text-sm">Кандидат медицинских наук</p>
                      <p className="text-xs text-[#64748b]">Диссертация 2009 г. — диагностика и профилактика нарушений осанки</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Resources */}
          <section className="bg-[#f8fafc] rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-[#0f172a] mb-6 text-center" style={{ fontFamily: 'Manrope' }}>Дополнительные ресурсы</h2>
            <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <a 
                href="https://isneuro.ru" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white rounded-xl p-4 border border-[#e2e8f0] hover:border-[#14b8a6]/30 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 bg-[#0B132B] rounded-lg flex items-center justify-center">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a] text-sm">Институт спортивной неврологии</p>
                  <p className="text-xs text-[#64748b]">isneuro.ru</p>
                </div>
              </a>
              <a 
                href="https://sophiatria.ru/kossvv/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-white rounded-xl p-4 border border-[#e2e8f0] hover:border-[#14b8a6]/30 hover:shadow-md transition-all"
              >
                <div className="w-10 h-10 bg-[#14b8a6] rounded-lg flex items-center justify-center">
                  <Stethoscope className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#0f172a] text-sm">Клиника «Софиатрия»</p>
                  <p className="text-xs text-[#64748b]">sophiatria.ru</p>
                </div>
              </a>
            </div>
          </section>

        </div>
      </div>
    </>
  );
};

// ============ B2B PAGE ============
const B2BPage = () => {
  const benefits = [
    { title: "Подбор комплекта", desc: "Поможем собрать комплект оборудования под задачи вашей клиники, фитнес-центра или реабилитационного пространства" },
    { title: "Обучение персонала", desc: "Проводим обучение правильному применению оборудования для вашей команды" },
    { title: "Консультационная поддержка", desc: "Отвечаем на вопросы и помогаем решать сложные случаи" },
    { title: "Специальные условия", desc: "Обсуждаем индивидуальные условия сотрудничества для клиник и центров" }
  ];

  return (
    <>
      <SEO page="b2b" />
      <div className="pt-28 pb-20" data-testid="b2b-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-[#0d9488] font-medium text-sm tracking-wide uppercase mb-3">Для бизнеса</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Для клиник, спорта и реабилитационных центров</h1>
          <p className="text-xl text-[#475569] max-w-3xl mx-auto">Помогаем оснащать кабинеты реабилитации, фитнес-центры и спортивные пространства проверенным оборудованием</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>Что мы предлагаем</h2>
            <div className="space-y-4">
              {benefits.map((item, i) => (
                <div key={i} className="flex items-start gap-4 p-5 bg-[#f8fafc] rounded-2xl">
                  <CheckCircle className="w-6 h-6 text-[#14b8a6] flex-shrink-0 mt-0.5" />
                  <div><div className="font-semibold text-[#0f172a]">{item.title}</div><div className="text-[#475569] text-sm mt-1">{item.desc}</div></div>
                </div>
              ))}
            </div>
          </div>
          <div><ConsultationForm isB2B={true} /></div>
        </div>
      </div>
    </div>
    </>
  );
};

// ============ CONTACT PAGE ============
const ContactPage = () => {
  return (
    <>
      <SEO page="contact" />
      <div className="pt-28 pb-20" data-testid="contact-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-[#0d9488] font-medium text-sm tracking-wide uppercase mb-3">Консультация</p>
          <h1 className="text-4xl md:text-5xl font-bold text-[#0f172a] mb-4" style={{ fontFamily: 'Manrope' }}>Получить консультацию</h1>
          <p className="text-xl text-[#475569] max-w-2xl mx-auto">Расскажите о вашей задаче — поможем подобрать подходящее решение</p>
        </div>
        <div className="grid lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <p className="text-[#475569] leading-relaxed">Не нужно разбираться в оборудовании самостоятельно. Опишите вашу ситуацию или задачу — мы зададим уточняющие вопросы и порекомендуем подходящие варианты.</p>
            <div className="space-y-6">
              {[
                { icon: MapPin, title: "Адрес", value: "г. Москва, ул. Лужники, 24, стр. 24", sub: "Клиника доктора Косса — консультации и тестирование" },
                { icon: Phone, title: "Телефон", value: "+7 (926) 599-52-52", sub: "Пн-Пт: 10:00 — 19:00", link: "tel:+79265995252" },
                { icon: Mail, title: "Email", value: "info@kosslab.ru", link: "mailto:info@kosslab.ru" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-[#14b8a6]/10 rounded-xl flex items-center justify-center flex-shrink-0"><item.icon className="w-5 h-5 text-[#0d9488]" /></div>
                  <div>
                    <div className="font-semibold text-[#0f172a] mb-1">{item.title}</div>
                    {item.link ? <a href={item.link} className="text-[#0d9488] hover:underline">{item.value}</a> : <div className="text-[#475569]">{item.value}</div>}
                    {item.sub && <div className="text-[#94a3b8] text-sm mt-1">{item.sub}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div><ConsultationForm /></div>
        </div>
      </div>
    </div>
    </>
  );
};

// ============ LEGAL PAGES ============
const PolicyLayout = ({ title, children }) => (
  <div className="pt-28 pb-20 bg-[#f8fafc] min-h-screen">
    <div className="container mx-auto px-4 md:px-8 max-w-4xl">
      <h1 className="text-3xl md:text-4xl font-bold text-[#0f172a] mb-8" style={{ fontFamily: 'Manrope' }}>{title}</h1>
      <div className="bg-white rounded-2xl p-8 md:p-12 shadow-sm border border-[#e2e8f0] prose prose-slate max-w-none">
        {children}
      </div>
    </div>
  </div>
);

const PrivacyPolicyPage = () => (
  <>
    <SEO page="privacy" />
    <PolicyLayout title="Политика конфиденциальности">
    <p className="text-sm text-[#64748b] mb-6">Дата последнего обновления: {new Date().toLocaleDateString('ru-RU')}</p>
    
    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">1. Общие положения</h2>
    <p className="text-[#475569] mb-4">
      Настоящая Политика конфиденциальности (далее — «Политика») определяет порядок обработки и защиты персональных данных пользователей сайта {LEGAL_INFO.site} (далее — «Сайт»), принадлежащего {LEGAL_INFO.company} (ИНН: {LEGAL_INFO.inn}, ОГРНИП: {LEGAL_INFO.ogrnip}).
    </p>
    <p className="text-[#475569] mb-4">
      Использование Сайта означает безоговорочное согласие пользователя с настоящей Политикой и указанными в ней условиями обработки персональных данных.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">2. Персональные данные, подлежащие обработке</h2>
    <p className="text-[#475569] mb-4">В рамках настоящей Политики под «персональными данными» понимаются:</p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Фамилия, имя, отчество</li>
      <li>Контактный телефон</li>
      <li>Адрес электронной почты (email)</li>
      <li>Данные, автоматически передаваемые в процессе использования Сайта (IP-адрес, информация из cookies, данные о браузере и устройстве)</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">3. Цели обработки персональных данных</h2>
    <p className="text-[#475569] mb-4">Персональные данные обрабатываются в следующих целях:</p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Обработка входящих запросов и заявок</li>
      <li>Оформление и исполнение заказов</li>
      <li>Предоставление консультаций по продукции</li>
      <li>Информирование о статусе заказа</li>
      <li>Улучшение качества обслуживания</li>
      <li>Проведение статистических исследований</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">4. Защита персональных данных</h2>
    <p className="text-[#475569] mb-4">
      Оператор принимает необходимые организационные и технические меры для защиты персональных данных от неправомерного или случайного доступа, уничтожения, изменения, блокирования, копирования, распространения, а также от иных неправомерных действий третьих лиц.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">5. Права пользователя</h2>
    <p className="text-[#475569] mb-4">Пользователь имеет право:</p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Получить информацию об обработке своих персональных данных</li>
      <li>Требовать уточнения, блокирования или уничтожения персональных данных</li>
      <li>Отозвать согласие на обработку персональных данных</li>
    </ul>
    <p className="text-[#475569] mb-4">
      Для реализации указанных прав необходимо направить запрос на email: {LEGAL_INFO.email}
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">6. Контактная информация</h2>
    <p className="text-[#475569] mb-4">
      {LEGAL_INFO.company}<br />
      ИНН: {LEGAL_INFO.inn}<br />
      ОГРНИП: {LEGAL_INFO.ogrnip}<br />
      Email: {LEGAL_INFO.email}
    </p>
  </PolicyLayout>
  </>
);

const TermsPage = () => (
  <>
    <SEO page="terms" />
    <PolicyLayout title="Пользовательское соглашение">
    <p className="text-sm text-[#64748b] mb-6">Дата последнего обновления: {new Date().toLocaleDateString('ru-RU')}</p>
    
    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">1. Общие положения</h2>
    <p className="text-[#475569] mb-4">
      Настоящее Пользовательское соглашение (далее — «Соглашение») регулирует отношения между {LEGAL_INFO.company} (далее — «Администрация») и пользователем сайта {LEGAL_INFO.site} (далее — «Пользователь»).
    </p>
    <p className="text-[#475569] mb-4">
      Использование Сайта означает полное и безоговорочное принятие Пользователем настоящего Соглашения.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">2. Предмет соглашения</h2>
    <p className="text-[#475569] mb-4">
      Администрация предоставляет Пользователю доступ к информации о товарах, возможность оформления заказов и получения консультаций по продукции для восстановления и реабилитации.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">3. Права и обязанности сторон</h2>
    <p className="text-[#475569] mb-4"><strong>Администрация обязуется:</strong></p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Предоставлять достоверную информацию о товарах и услугах</li>
      <li>Обрабатывать заказы и заявки в разумные сроки</li>
      <li>Соблюдать конфиденциальность персональных данных</li>
    </ul>
    <p className="text-[#475569] mb-4"><strong>Пользователь обязуется:</strong></p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Предоставлять достоверную информацию при оформлении заказов</li>
      <li>Не использовать Сайт в противоправных целях</li>
      <li>Не нарушать работоспособность Сайта</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">4. Ограничение ответственности</h2>
    <p className="text-[#475569] mb-4">
      Информация на Сайте носит справочный характер и не является медицинской рекомендацией. Перед использованием оборудования рекомендуется консультация специалиста.
    </p>
    <p className="text-[#475569] mb-4">
      Администрация не несёт ответственности за возможные убытки, связанные с использованием или невозможностью использования Сайта.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">5. Изменение условий</h2>
    <p className="text-[#475569] mb-4">
      Администрация оставляет за собой право изменять настоящее Соглашение без предварительного уведомления. Актуальная версия всегда доступна на Сайте.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">6. Контактная информация</h2>
    <p className="text-[#475569] mb-4">
      {LEGAL_INFO.company}<br />
      Email: {LEGAL_INFO.email}
    </p>
  </PolicyLayout>
  </>
);

const OfferPage = () => (
  <>
    <SEO page="offer" />
    <PolicyLayout title="Публичная оферта">
    <p className="text-sm text-[#64748b] mb-6">Дата последнего обновления: {new Date().toLocaleDateString('ru-RU')}</p>
    
    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">1. Общие положения</h2>
    <p className="text-[#475569] mb-4">
      Настоящий документ является официальным предложением (публичной офертой) {LEGAL_INFO.company} (ИНН: {LEGAL_INFO.inn}, ОГРНИП: {LEGAL_INFO.ogrnip}) заключить договор купли-продажи товаров на условиях, изложенных ниже.
    </p>
    <p className="text-[#475569] mb-4">
      В соответствии со статьёй 437 Гражданского кодекса РФ данный документ является публичной офертой, и в случае принятия изложенных ниже условий лицо, производящее акцепт этой оферты, осуществляет оплату товара в соответствии с условиями настоящего договора.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">2. Термины и определения</h2>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li><strong>Продавец</strong> — {LEGAL_INFO.company}</li>
      <li><strong>Покупатель</strong> — физическое или юридическое лицо, оформившее заказ</li>
      <li><strong>Товар</strong> — оборудование для восстановления и реабилитации, представленное на сайте {LEGAL_INFO.site}</li>
      <li><strong>Заказ</strong> — оформленная заявка на приобретение товара</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">3. Предмет договора</h2>
    <p className="text-[#475569] mb-4">
      Продавец обязуется передать в собственность Покупателю, а Покупатель — принять и оплатить товар в соответствии с условиями настоящей оферты.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">4. Оформление заказа</h2>
    <p className="text-[#475569] mb-4">
      Заказ оформляется через сайт {LEGAL_INFO.site} или по телефону {LEGAL_INFO.phone}. При оформлении заказа Покупатель указывает:
    </p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>ФИО или наименование организации</li>
      <li>Контактный телефон</li>
      <li>Адрес электронной почты</li>
      <li>Адрес доставки</li>
      <li>Выбранные товары</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">5. Цена и оплата</h2>
    <p className="text-[#475569] mb-4">
      Цены на товары указаны на сайте в рублях РФ. Продавец оставляет за собой право изменять цены без предварительного уведомления. Цена, действующая на момент оформления заказа, изменению не подлежит.
    </p>
    <p className="text-[#475569] mb-4">
      Оплата производится одним из следующих способов:
    </p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-2">
      <li>Онлайн-оплата банковской картой через T-Bank</li>
      <li>Безналичный расчёт (для юридических лиц)</li>
    </ul>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">6. Доставка</h2>
    <p className="text-[#475569] mb-4">
      Доставка осуществляется по всей территории России. Сроки и стоимость доставки согласовываются индивидуально при оформлении заказа.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">7. Гарантии и возврат</h2>
    <p className="text-[#475569] mb-4">
      На все товары распространяется гарантия производителя.
    </p>
    <p className="text-[#475569] mb-4">
      <strong>Важно:</strong> В соответствии с Постановлением Правительства РФ от 31.12.2020 № 2463, 
      реализуемые нами товары (медицинские изделия, товары для профилактики и лечения заболеваний в домашних условиях, 
      ортопедические изделия) относятся к категории товаров надлежащего качества, <strong>не подлежащих возврату или обмену</strong>.
    </p>
    <p className="text-[#475569] mb-4">
      Возврат или обмен товара возможен в следующих случаях:
    </p>
    <ul className="list-disc pl-6 text-[#475569] mb-4 space-y-1">
      <li>Обнаружен производственный брак или дефект</li>
      <li>Товар не соответствует описанию на сайте</li>
      <li>Нарушена комплектность товара</li>
    </ul>
    <p className="text-[#475569] mb-4">
      Для возврата товара ненадлежащего качества свяжитесь с нами по телефону +7 (926) 599-52-52 или email info@kosslab.ru в течение гарантийного срока.
    </p>

    <h2 className="text-xl font-semibold text-[#0f172a] mt-8 mb-4">8. Реквизиты продавца</h2>
    <p className="text-[#475569] mb-4">
      {LEGAL_INFO.company}<br />
      ИНН: {LEGAL_INFO.inn}<br />
      ОГРНИП: {LEGAL_INFO.ogrnip}<br />
      Email: {LEGAL_INFO.email}
    </p>
  </PolicyLayout>
  </>
);

// ============ ADMIN PAGE ============
const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('orders');
  const [stats, setStats] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Product form state
  const [productForm, setProductForm] = useState({
    name: '', description: '', price: '', category_id: '', 
    image_url: '', features: '', is_flagship: false, in_stock: true
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  // Check if already logged in
  useEffect(() => {
    const token = sessionStorage.getItem('admin_token');
    if (token === 'admin_authenticated') {
      setIsAuthenticated(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    }
  }, [isAuthenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const res = await axios.post(`${API}/admin/login`, { password });
      if (res.data.success) {
        sessionStorage.setItem('admin_token', res.data.token);
        setIsAuthenticated(true);
      }
    } catch (error) {
      setLoginError('Неверный пароль');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setPassword('');
  };

  const fetchData = async () => {
    try {
      const [statsRes, reviewsRes, productsRes, catsRes, ordersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/reviews?approved_only=false`),
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/orders`)
      ]);
      setStats(statsRes.data);
      setReviews(reviewsRes.data);
      setProducts(productsRes.data);
      setCategories(catsRes.data);
      setOrders(ordersRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOrderStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/orders/${id}/status`, { status });
      toast.success('Статус обновлён');
      fetchData();
    } catch (error) {
      toast.error('Ошибка');
    }
  };

  const handleDeleteOrder = async (id) => {
    if (!window.confirm('Удалить заказ?')) return;
    try {
      await axios.delete(`${API}/orders/${id}`);
      toast.success('Заказ удалён');
      fetchData();
    } catch (error) {
      toast.error('Ошибка');
    }
  };

  const handleApproveReview = async (id) => {
    try {
      await axios.patch(`${API}/reviews/${id}/approve`);
      toast.success('Отзыв одобрен');
      fetchData();
    } catch (error) {
      toast.error('Ошибка при одобрении отзыва');
    }
  };

  const handleDeleteReview = async (id) => {
    if (!window.confirm('Удалить отзыв?')) return;
    try {
      await axios.delete(`${API}/reviews/${id}`);
      toast.success('Отзыв удалён');
      fetchData();
    } catch (error) {
      toast.error('Ошибка при удалении');
    }
  };

  const handleToggleFeatured = async (id) => {
    try {
      const res = await axios.patch(`${API}/reviews/${id}/feature`);
      toast.success(res.data.is_featured ? 'Добавлено на главную' : 'Убрано с главной');
      fetchData();
    } catch (error) {
      toast.error('Ошибка');
    }
  };

  const handleImageFile = useCallback(async (file) => {
    if (file && file.type.startsWith('image/')) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Файл слишком большой. Максимум 5MB');
        return;
      }
      
      // Show preview immediately
      const reader = new FileReader();
      reader.onload = (e) => setImagePreview(e.target.result);
      reader.readAsDataURL(file);
      
      // Upload to server
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/upload`, formData);
        setProductForm(prev => ({ ...prev, image_url: res.data.url }));
        toast.success('Изображение загружено');
      } catch (error) {
        toast.error('Ошибка загрузки изображения');
      }
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    handleImageFile(e.dataTransfer.files[0]);
  }, [handleImageFile]);

  const openProductForm = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setProductForm({
        name: product.name,
        description: product.description,
        price: product.price.toString(),
        category_id: product.category_id,
        image_url: product.image_url || '',
        features: (product.features || []).join('\n'),
        is_flagship: product.is_flagship || false,
        in_stock: product.in_stock !== false
      });
      setImagePreview(product.image_url);
    } else {
      setEditingProduct(null);
      setProductForm({ name: '', description: '', price: '', category_id: '', image_url: '', features: '', is_flagship: false, in_stock: true });
      setImagePreview(null);
    }
    setShowProductForm(true);
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    const data = {
      name: productForm.name,
      description: productForm.description,
      price: parseFloat(productForm.price),
      category_id: productForm.category_id,
      image_url: productForm.image_url,
      features: productForm.features.split('\n').filter(f => f.trim()),
      is_flagship: productForm.is_flagship,
      in_stock: productForm.in_stock
    };

    try {
      if (editingProduct) {
        await axios.put(`${API}/admin/products/${editingProduct.id}`, data);
        toast.success('Товар обновлён');
      } else {
        await axios.post(`${API}/admin/products`, data);
        toast.success('Товар добавлен');
      }
      setShowProductForm(false);
      fetchData();
    } catch (error) {
      toast.error('Ошибка при сохранении');
    }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Удалить товар?')) return;
    try {
      await axios.delete(`${API}/admin/products/${id}`);
      toast.success('Товар удалён');
      fetchData();
    } catch (error) {
      toast.error('Ошибка при удалении');
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center pt-24 pb-12">
        <div className="bg-white rounded-2xl p-8 shadow-lg border border-[#e2e8f0] w-full max-w-md">
          <h1 className="text-2xl font-bold text-[#0f172a] mb-6 text-center" style={{ fontFamily: 'Manrope' }}>
            Вход в админ-панель
          </h1>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">Пароль</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Введите пароль"
                className="input-premium"
                required
                data-testid="admin-password"
              />
            </div>
            {loginError && (
              <p className="text-red-500 text-sm">{loginError}</p>
            )}
            <button
              type="submit"
              disabled={loginLoading}
              className="w-full btn-navy h-12"
              data-testid="admin-login-btn"
            >
              {loginLoading ? 'Вход...' : 'Войти'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#14b8a6] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f8fafc] pt-24 pb-12">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-[#0f172a]" style={{ fontFamily: 'Manrope' }}>
            Админ-панель
          </h1>
          <button
            onClick={handleLogout}
            className="px-4 py-2 text-sm text-[#64748b] hover:text-[#0f172a] hover:bg-[#f1f5f9] rounded-lg transition-colors"
          >
            Выйти
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#0f172a]">{stats.products}</div>
              <div className="text-sm text-[#64748b]">Товаров</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#0f172a]">{stats.categories}</div>
              <div className="text-sm text-[#64748b]">Категорий</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#f59e0b]">{stats.pending_reviews}</div>
              <div className="text-sm text-[#64748b]">На модерации</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#0f172a]">{stats.reviews}</div>
              <div className="text-sm text-[#64748b]">Всего отзывов</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#0f172a]">{stats.orders}</div>
              <div className="text-sm text-[#64748b]">Заказов</div>
            </div>
            <div className="bg-white rounded-xl p-4 border border-[#e2e8f0]">
              <div className="text-2xl font-bold text-[#0f172a]">{stats.consultations}</div>
              <div className="text-sm text-[#64748b]">Консультаций</div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('reviews')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'reviews' ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
            }`}
          >
            Отзывы {stats?.pending_reviews > 0 && <span className="ml-1 px-2 py-0.5 bg-[#f59e0b] text-white text-xs rounded-full">{stats.pending_reviews}</span>}
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'products' ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
            }`}
          >
            Товары
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'orders' ? 'bg-[#0f172a] text-white' : 'bg-white text-[#64748b] hover:bg-[#f1f5f9]'
            }`}
          >
            Заказы {orders.length > 0 && <span className="ml-1 px-2 py-0.5 bg-[#14b8a6] text-white text-xs rounded-full">{orders.length}</span>}
          </button>
        </div>

        {/* Orders Tab */}
        {activeTab === 'orders' && (
          <div className="space-y-4">
            {orders.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-[#64748b] border border-[#e2e8f0]">
                Заказов пока нет
              </div>
            ) : (
              orders.map((order) => (
                <div key={order.id} className={`bg-white rounded-xl p-6 border border-[#e2e8f0] ${
                  order.status === 'new' ? 'border-l-4 border-l-[#14b8a6]' : 
                  order.status === 'processing' ? 'border-l-4 border-l-[#f59e0b]' :
                  order.status === 'completed' ? 'border-l-4 border-l-[#22c55e]' :
                  'border-l-4 border-l-[#ef4444]'
                }`}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-semibold text-[#0f172a]">{order.customer_name}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          order.status === 'new' ? 'bg-[#14b8a6]/10 text-[#14b8a6]' :
                          order.status === 'processing' ? 'bg-[#f59e0b]/10 text-[#f59e0b]' :
                          order.status === 'completed' ? 'bg-[#22c55e]/10 text-[#22c55e]' :
                          'bg-[#ef4444]/10 text-[#ef4444]'
                        }`}>
                          {order.status === 'new' ? 'Новый' : 
                           order.status === 'processing' ? 'В обработке' :
                           order.status === 'completed' ? 'Завершён' : 'Отменён'}
                        </span>
                        {order.payment_status && (
                          <span className={`px-2 py-0.5 text-xs rounded-full ${
                            order.payment_status === 'paid' ? 'bg-[#22c55e]/10 text-[#22c55e]' :
                            order.payment_status === 'pending' ? 'bg-[#f59e0b]/10 text-[#f59e0b]' :
                            order.payment_status === 'refunded' ? 'bg-[#8b5cf6]/10 text-[#8b5cf6]' :
                            'bg-[#64748b]/10 text-[#64748b]'
                          }`}>
                            {order.payment_status === 'paid' ? '💳 Оплачено' : 
                             order.payment_status === 'pending' ? '⏳ Ожидает оплаты' :
                             order.payment_status === 'refunded' ? '↩️ Возвращено' : order.payment_status}
                          </span>
                        )}
                        <span className="text-[#0f172a] font-bold">{order.total_price?.toLocaleString()} ₽</span>
                      </div>
                      <p className="text-[#475569] mb-1"><strong>Товар:</strong> {order.product_name}</p>
                      <p className="text-[#475569] mb-1"><strong>Телефон:</strong> <a href={`tel:${order.customer_phone}`} className="text-[#14b8a6] hover:underline">{order.customer_phone}</a></p>
                      {order.customer_email && (
                        <p className="text-[#475569] mb-1"><strong>Email:</strong> <a href={`mailto:${order.customer_email}`} className="text-[#14b8a6] hover:underline">{order.customer_email}</a></p>
                      )}
                      {order.notes && (
                        <p className="text-[#475569] mb-1"><strong>Комментарий:</strong> {order.notes}</p>
                      )}
                      <p className="text-xs text-[#94a3b8] mt-2">{new Date(order.created_at).toLocaleString('ru-RU')}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      <select
                        value={order.status}
                        onChange={(e) => handleUpdateOrderStatus(order.id, e.target.value)}
                        className="text-sm border border-[#e2e8f0] rounded-lg px-2 py-1 bg-white"
                      >
                        <option value="new">Новый</option>
                        <option value="processing">В обработке</option>
                        <option value="completed">Завершён</option>
                        <option value="cancelled">Отменён</option>
                      </select>
                      {order.payment_id && order.payment_status === 'paid' && (
                        <button
                          onClick={async () => {
                            if (window.confirm('Вернуть деньги клиенту? Это действие необратимо.')) {
                              try {
                                const res = await axios.post(`${API}/payments/${order.payment_id}/refund`);
                                if (res.data.success) {
                                  toast.success('Возврат выполнен успешно');
                                  fetchData();
                                } else {
                                  toast.error(res.data.message || 'Ошибка возврата');
                                }
                              } catch (err) {
                                toast.error('Ошибка возврата');
                              }
                            }
                          }}
                          className="px-3 py-2 bg-[#8b5cf6] text-white text-xs rounded-lg hover:bg-[#7c3aed] transition-colors"
                          title="Вернуть деньги"
                        >
                          ↩️ Возврат
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteOrder(order.id)}
                        className="p-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Reviews Tab */}
        {activeTab === 'reviews' && (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center text-[#64748b] border border-[#e2e8f0]">
                Отзывов пока нет
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className={`bg-white rounded-xl p-6 border ${review.is_approved ? 'border-[#e2e8f0]' : 'border-[#f59e0b]'} ${review.is_featured ? 'ring-2 ring-[#14b8a6]' : ''}`}>
                  <div className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <span className="font-semibold text-[#0f172a]">{review.author_name}</span>
                        <div className="flex gap-0.5">
                          {[1,2,3,4,5].map(s => (
                            <Star key={s} className={`w-4 h-4 ${s <= review.rating ? 'fill-[#f59e0b] text-[#f59e0b]' : 'text-[#e2e8f0]'}`} />
                          ))}
                        </div>
                        {!review.is_approved && (
                          <span className="px-2 py-0.5 bg-[#fef3c7] text-[#92400e] text-xs rounded-full">На модерации</span>
                        )}
                        {review.is_featured && (
                          <span className="px-2 py-0.5 bg-[#14b8a6] text-white text-xs rounded-full">На главной</span>
                        )}
                      </div>
                      <p className="text-[#475569] mb-2">{review.text}</p>
                      {review.photo_url && (
                        <img src={review.photo_url} alt="Фото" className="w-24 h-24 object-cover rounded-lg" />
                      )}
                      <p className="text-xs text-[#94a3b8] mt-2">{new Date(review.created_at).toLocaleDateString('ru-RU')}</p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {!review.is_approved && (
                        <button
                          onClick={() => handleApproveReview(review.id)}
                          className="p-2 bg-[#14b8a6] text-white rounded-lg hover:bg-[#0d9488] transition-colors"
                          title="Одобрить"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                      )}
                      {review.is_approved && (
                        <button
                          onClick={() => handleToggleFeatured(review.id)}
                          className={`p-2 rounded-lg transition-colors ${review.is_featured ? 'bg-[#f59e0b] text-white hover:bg-[#d97706]' : 'bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]'}`}
                          title={review.is_featured ? 'Убрать с главной' : 'Показать на главной'}
                        >
                          <Star className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteReview(review.id)}
                        className="p-2 bg-[#ef4444] text-white rounded-lg hover:bg-[#dc2626] transition-colors"
                        title="Удалить"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Products Tab */}
        {activeTab === 'products' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <p className="text-[#64748b]">{products.length} товаров</p>
              <button
                onClick={() => openProductForm()}
                className="btn-teal inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> Добавить товар
              </button>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map((product) => (
                <div key={product.id} className="bg-white rounded-xl border border-[#e2e8f0] overflow-hidden">
                  <div className="aspect-video bg-[#f8fafc] relative">
                    <img 
                      src={product.image_url} 
                      alt={product.name}
                      className="w-full h-full object-contain p-4"
                    />
                    {product.is_flagship && (
                      <span className="absolute top-2 left-2 px-2 py-1 bg-[#14b8a6] text-white text-xs rounded-lg">
                        Флагман
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-[#0f172a] mb-1 line-clamp-1">{product.name}</h3>
                    <p className="text-[#14b8a6] font-bold mb-3">{product.price?.toLocaleString('ru-RU')} ₽</p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openProductForm(product)}
                        className="flex-1 py-2 bg-[#f1f5f9] text-[#475569] rounded-lg hover:bg-[#e2e8f0] transition-colors text-sm"
                      >
                        Редактировать
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="p-2 bg-[#fef2f2] text-[#ef4444] rounded-lg hover:bg-[#fee2e2] transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Product Form Modal */}
      <Dialog open={showProductForm} onOpenChange={setShowProductForm}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
              {editingProduct ? 'Редактировать товар' : 'Добавить товар'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSaveProduct} className="space-y-5 mt-4">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-[#374151] mb-2">Изображение</label>
              {!imagePreview ? (
                <div
                  onDrop={handleDrop}
                  onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                  className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                    isDragging ? 'border-[#14b8a6] bg-[#f0fdfa]' : 'border-[#e2e8f0] hover:border-[#14b8a6]/50'
                  }`}
                >
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageFile(e.target.files[0])}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <Upload className="w-8 h-8 text-[#64748b] mx-auto mb-2" />
                  <p className="text-[#64748b]">Перетащите изображение или нажмите</p>
                  <p className="text-xs text-[#94a3b8] mt-3">Или вставьте URL:</p>
                  <input
                    type="url"
                    value={productForm.image_url?.startsWith('data:') ? '' : productForm.image_url}
                    onChange={(e) => { setProductForm(p => ({ ...p, image_url: e.target.value })); if(e.target.value) setImagePreview(e.target.value); }}
                    placeholder="https://..."
                    className="input-premium mt-1"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ) : (
                <div className="relative">
                  <img src={imagePreview} alt="Preview" className="w-full h-48 object-contain bg-[#f8fafc] rounded-xl" />
                  <button
                    type="button"
                    onClick={() => { setImagePreview(null); setProductForm(p => ({ ...p, image_url: '' })); }}
                    className="absolute top-2 right-2 p-2 bg-white rounded-lg shadow-md hover:bg-red-50 transition-colors"
                    title="Удалить изображение"
                  >
                    <X className="w-4 h-4 text-red-500" />
                  </button>
                  <p className="text-xs text-green-600 mt-2 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> Изображение загружено
                  </p>
                </div>
              )}
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Название *</label>
                <input
                  value={productForm.name}
                  onChange={(e) => setProductForm(p => ({ ...p, name: e.target.value }))}
                  className="input-premium"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#374151] mb-1.5">Цена (₽) *</label>
                <input
                  type="number"
                  value={productForm.price}
                  onChange={(e) => setProductForm(p => ({ ...p, price: e.target.value }))}
                  className="input-premium"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">Категория *</label>
              <select
                value={productForm.category_id}
                onChange={(e) => setProductForm(p => ({ ...p, category_id: e.target.value }))}
                className="input-premium"
                required
              >
                <option value="">Выберите категорию</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">Описание *</label>
              <Textarea
                value={productForm.description}
                onChange={(e) => setProductForm(p => ({ ...p, description: e.target.value }))}
                rows={3}
                className="rounded-xl border-[#e2e8f0]"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#374151] mb-1.5">Особенности (каждая с новой строки)</label>
              <Textarea
                value={productForm.features}
                onChange={(e) => setProductForm(p => ({ ...p, features: e.target.value }))}
                rows={3}
                placeholder="Одна особенность на строку"
                className="rounded-xl border-[#e2e8f0]"
              />
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={productForm.is_flagship}
                onChange={(e) => setProductForm(p => ({ ...p, is_flagship: e.target.checked }))}
                className="w-5 h-5 rounded border-[#e2e8f0] text-[#14b8a6] focus:ring-[#14b8a6]"
              />
              <span className="text-[#374151]">Флагманский товар (показывать на главной)</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={productForm.in_stock}
                onChange={(e) => setProductForm(p => ({ ...p, in_stock: e.target.checked }))}
                className="w-5 h-5 rounded border-[#e2e8f0] text-[#22c55e] focus:ring-[#22c55e]"
              />
              <span className="text-[#374151]">В наличии</span>
            </label>

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={() => setShowProductForm(false)} className="flex-1 btn-outline">
                Отмена
              </button>
              <button type="submit" className="flex-1 btn-teal">
                {editingProduct ? 'Сохранить' : 'Добавить'}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ MAIN APP ============
function App() {
  return (
    <HelmetProvider>
      <CartProvider>
        <BrowserRouter>
          {/* Animated Background */}
          <div className="animated-bg" aria-hidden="true"></div>
          <div className="floating-orbs" aria-hidden="true">
            <div className="orb orb-1"></div>
            <div className="orb orb-2"></div>
            <div className="orb orb-3"></div>
          </div>
          
          <div className="App min-h-screen flex flex-col relative z-0">
            <Toaster position="top-center" richColors />
            <Navigation />
            <main className="flex-1">
              <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/catalog" element={<CatalogPage />} />
                <Route path="/product/:slug" element={<ProductPage />} />
                <Route path="/about" element={<AboutPage />} />
                <Route path="/publications" element={<PublicationsPage />} />
                <Route path="/b2b" element={<B2BPage />} />
                <Route path="/contact" element={<ContactPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/privacy" element={<PrivacyPolicyPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/offer" element={<OfferPage />} />
              </Routes>
            </main>
            <Footer />
            <ChatWidget />
          </div>
        </BrowserRouter>
      </CartProvider>
    </HelmetProvider>
  );
}

export default App;
